import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  Sparkles,
  ArrowRight,
  Play,
  Plane,
  TrainFront,
  BedDouble,
  Calendar as CalendarIcon,
  User,
  Search,
  MapPin,
  ArrowRightLeft,
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  Star,
  Heart,
  ThumbsUp,
  ThumbsDown,
  Share2,
  Mail,
  Phone,
  Facebook,
  Instagram,
  Twitter,
  Hexagon,
  Quote,
  Smartphone,
  Download,
  ArrowUpRight,
  Briefcase,
  Globe,
  MessageCircle,
  Plus,
  Minus,
  Trash2,
  Wallet,
  CreditCard,
  Menu
} from 'lucide-react';
import { NAV_LINKS, FEATURES } from './constants';
import { fetchPackages, fetchDestinations, fetchArticles, Package, Destination, Article } from './api';

// Lazy load secondary pages to reduce initial bundle size
const ServicesPage = React.lazy(() => import('./ServicesPage'));
const FlightBookingPage = React.lazy(() => import('./FlightBookingPage'));
const HotelBookingPage = React.lazy(() => import('./HotelBookingPage'));
const CarRentalPage = React.lazy(() => import('./CarRentalPage'));
const DelegationReceptionPage = React.lazy(() => import('./DelegationReceptionPage'));
const VisaServicesPage = React.lazy(() => import('./VisaServicesPage'));
const TrainBookingPage = React.lazy(() => import('./TrainBookingPage'));
const FootballBookingPage = React.lazy(() => import('./FootballBookingPage'));
const TripOrganizingPage = React.lazy(() => import('./TripOrganizingPage'));
const FAQPage = React.lazy(() => import('./FAQPage'));
const ContactPage = React.lazy(() => import('./ContactPage'));
const AboutPage = React.lazy(() => import('./AboutPage'));
const TermsPage = React.lazy(() => import('./TermsPage'));
const PrivacyPage = React.lazy(() => import('./PrivacyPage'));

// Loading component for suspense fallback
const PageLoader = () => (
  <div className="min-h-screen bg-white flex items-center justify-center z-50">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
    </div>
  </div>
);
import Navbar from './Navbar';
import Footer from './Footer';
import Portal from './Portal';
import { TRANSLATIONS } from './translations';
import SEO from './components/SEO';
import { trackEvent } from './useEventTracking';
import LazyImage from './components/LazyImage';
const ReactMarkdown = React.lazy(() => import('react-markdown'));

// --- Translations ---
// Moved to translations.ts

// --- Mock Data ---

const getLocalizedPaymentOptions = (lang: 'en' | 'ar') => {
  if (lang === 'ar') {
    return [
      {
        id: 1,
        title: "قسّم قيمة حجزك على 4 دفعات",
        subtitle: "واكتشف العالم وأنت مرتاح",
        image: "/assets/images/partners/boubyan_medium.webp",
        imageSmall: "/assets/images/partners/boubyan_small.webp",
        logoText: "Deema"
      },
      {
        id: 2,
        title: "قسّم قيمة حجزك على 4 دفعات",
        subtitle: "واكتشف العالم وأنت مرتاح",
        image: "/assets/images/partners/tabby_medium.webp",
        imageSmall: "/assets/images/partners/tabby_small.webp",
        logoText: "tabby"
      },
      {
        id: 3,
        title: "نقاط كومو؛ تعطيك أكثر",
        subtitle: "أضف أو حوّل نقاط كومو!",
        image: "/assets/images/como-points.webp",
        imageSmall: "/assets/images/como-points_small.webp",
        logoText: "COMO"
      }
    ];
  }
  return [
    {
      id: 1,
      title: "Split your booking into 4 payments",
      subtitle: "Discover the world with comfort",
      image: "/assets/images/partners/boubyan_medium.webp",
      imageSmall: "/assets/images/partners/boubyan_small.webp",
      logoText: "Deema"
    },
    {
      id: 2,
      title: "Split your booking into 4 payments",
      subtitle: "Discover the world with comfort",
      image: "/assets/images/partners/tabby_medium.webp",
      imageSmall: "/assets/images/partners/tabby_small.webp",
      logoText: "tabby"
    },
    {
      id: 3,
      title: "COMO Points; give you more",
      subtitle: "Add or transfer COMO points!",
      image: "/assets/images/como-points.webp",
      imageSmall: "/assets/images/como-points_small.webp",
      logoText: "COMO"
    }
  ];
};

const getLocalizedActivities = (lang: 'en' | 'ar') => {
  if (lang === 'ar') {
    return [
      {
        id: 7,
        title: "فندق هاري بوتر",
        subtitle: "ألمانيا",
        image: "/assets/images/hp.webp",
        imageSmall: "/assets/images/hp_small.jpg",
        video: "/assets/videos/hp.mp4",
        videoWebm: "/assets/videos/hp.webm",
        hasOverlay: false
      },
      {
        id: 1,
        title: "أرويا كروز",
        subtitle: "السعودية",
        image: "/assets/images/partners/aroya_medium.webp?v=2",
        logoTitle: "AROYA",
        logoSub: "CRUISES",
        promo: "35%",
        hasOverlay: true
      },
      {
        id: 2,
        title: "متنزه Six Flags مدينة القدية",
        subtitle: "الرياض، المملكة العربية السعودية",
        image: "/activities/sixflags.webp",
        imageMedium: "/assets/images/sixflags_medium.webp?v=2",
        imageSmall: "/assets/images/sixflags_small.webp",
        hasOverlay: false
      },
      {
        id: 8,
        title: "جزيرة ياس",
        subtitle: "أبوظبي، الإمارات",
        image: "/activities/yas.webp",
        imageMedium: "/assets/images/yas_medium.webp",
        hasOverlay: false
      }
    ];
  }
  return [
    {
      id: 7,
      title: "Harry Potter Hotel",
      subtitle: "Germany",
      image: "/assets/images/hp.webp",
      imageSmall: "/assets/images/hp_small.jpg",
      video: "/assets/videos/hp.mp4",
      videoWebm: "/assets/videos/hp.webm",
      hasOverlay: false
    },
    {
      id: 1,
      title: "Aroya Cruises",
      subtitle: "Saudi Arabia",
      image: "/assets/images/partners/aroya_medium.webp?v=2",
      logoTitle: "AROYA",
      logoSub: "CRUISES",
      promo: "35%",
      hasOverlay: true
    },
    {
      id: 2,
      title: "Six Flags Qiddiya City Park",
      subtitle: "Riyadh, Saudi Arabia",
      image: "/activities/sixflags.webp",
      imageMedium: "/assets/images/sixflags_medium.webp?v=2",
      imageSmall: "/assets/images/sixflags_small.webp",
      hasOverlay: false
    },
    {
      id: 8,
      title: "Yas Island",
      subtitle: "Abu Dhabi, UAE",
      image: "/activities/yas.webp",
      imageMedium: "/assets/images/yas_medium.webp",
      hasOverlay: false
    }
  ];
};

// Comprehensive Airport/City Database with coordinates for geolocation
interface LocationData {
  code: string;
  city: string;
  country: string;
  lat: number;
  lng: number;
}

const AIRPORTS_DATABASE: LocationData[] = [
  // Middle East
  { code: 'KWI', city: 'Kuwait City', country: 'Kuwait', lat: 29.2266, lng: 47.9689 },
  { code: 'DXB', city: 'Dubai', country: 'UAE', lat: 25.2532, lng: 55.3657 },
  { code: 'AUH', city: 'Abu Dhabi', country: 'UAE', lat: 24.433, lng: 54.651 },
  { code: 'DOH', city: 'Doha', country: 'Qatar', lat: 25.2731, lng: 51.6081 },
  { code: 'BAH', city: 'Manama', country: 'Bahrain', lat: 26.2708, lng: 50.6336 },
  { code: 'MCT', city: 'Muscat', country: 'Oman', lat: 23.5933, lng: 58.2844 },
  { code: 'RUH', city: 'Riyadh', country: 'Saudi Arabia', lat: 24.9578, lng: 46.6989 },
  { code: 'JED', city: 'Jeddah', country: 'Saudi Arabia', lat: 21.6796, lng: 39.1565 },
  { code: 'DMM', city: 'Dammam', country: 'Saudi Arabia', lat: 26.4712, lng: 49.7979 },
  { code: 'MED', city: 'Medina', country: 'Saudi Arabia', lat: 24.5534, lng: 39.7051 },
  { code: 'AMM', city: 'Amman', country: 'Jordan', lat: 31.7226, lng: 35.9932 },
  { code: 'BEY', city: 'Beirut', country: 'Lebanon', lat: 33.8209, lng: 35.4884 },
  { code: 'TLV', city: 'Tel Aviv', country: 'Israel', lat: 32.0055, lng: 34.8854 },
  { code: 'CAI', city: 'Cairo', country: 'Egypt', lat: 30.1219, lng: 31.4056 },
  { code: 'HRG', city: 'Hurghada', country: 'Egypt', lat: 27.1783, lng: 33.7994 },
  { code: 'SSH', city: 'Sharm El Sheikh', country: 'Egypt', lat: 27.9773, lng: 34.395 },

  // Europe
  { code: 'LHR', city: 'London', country: 'United Kingdom', lat: 51.47, lng: -0.4543 },
  { code: 'CDG', city: 'Paris', country: 'France', lat: 49.0097, lng: 2.5479 },
  { code: 'FCO', city: 'Rome', country: 'Italy', lat: 41.8003, lng: 12.2389 },
  { code: 'MXP', city: 'Milan', country: 'Italy', lat: 45.6306, lng: 8.7281 },
  { code: 'BCN', city: 'Barcelona', country: 'Spain', lat: 41.2971, lng: 2.0785 },
  { code: 'MAD', city: 'Madrid', country: 'Spain', lat: 40.4983, lng: -3.5676 },
  { code: 'AMS', city: 'Amsterdam', country: 'Netherlands', lat: 52.3086, lng: 4.7639 },
  { code: 'FRA', city: 'Frankfurt', country: 'Germany', lat: 50.0379, lng: 8.5622 },
  { code: 'MUC', city: 'Munich', country: 'Germany', lat: 48.3538, lng: 11.786 },
  { code: 'BER', city: 'Berlin', country: 'Germany', lat: 52.3667, lng: 13.5033 },
  { code: 'ZRH', city: 'Zurich', country: 'Switzerland', lat: 47.4647, lng: 8.5492 },
  { code: 'GVA', city: 'Geneva', country: 'Switzerland', lat: 46.2381, lng: 6.1089 },
  { code: 'VIE', city: 'Vienna', country: 'Austria', lat: 48.1103, lng: 16.5697 },
  { code: 'PRG', city: 'Prague', country: 'Czech Republic', lat: 50.1008, lng: 14.26 },
  { code: 'ATH', city: 'Athens', country: 'Greece', lat: 37.9364, lng: 23.9445 },
  { code: 'IST', city: 'Istanbul', country: 'Turkey', lat: 41.2753, lng: 28.7519 },
  { code: 'LIS', city: 'Lisbon', country: 'Portugal', lat: 38.7756, lng: -9.1354 },
  { code: 'CPH', city: 'Copenhagen', country: 'Denmark', lat: 55.618, lng: 12.656 },
  { code: 'OSL', city: 'Oslo', country: 'Norway', lat: 60.1939, lng: 11.1004 },
  { code: 'ARN', city: 'Stockholm', country: 'Sweden', lat: 59.6519, lng: 17.9186 },
  { code: 'HEL', city: 'Helsinki', country: 'Finland', lat: 60.3172, lng: 24.9633 },
  { code: 'DUB', city: 'Dublin', country: 'Ireland', lat: 53.4264, lng: -6.2499 },
  { code: 'BRU', city: 'Brussels', country: 'Belgium', lat: 50.9014, lng: 4.4844 },
  { code: 'WAW', city: 'Warsaw', country: 'Poland', lat: 52.1657, lng: 20.9671 },

  // Asia
  { code: 'SIN', city: 'Singapore', country: 'Singapore', lat: 1.3644, lng: 103.9915 },
  { code: 'HKG', city: 'Hong Kong', country: 'Hong Kong', lat: 22.308, lng: 113.9185 },
  { code: 'BKK', city: 'Bangkok', country: 'Thailand', lat: 13.6811, lng: 100.7472 },
  { code: 'KUL', city: 'Kuala Lumpur', country: 'Malaysia', lat: 2.7456, lng: 101.7072 },
  { code: 'DPS', city: 'Bali', country: 'Indonesia', lat: -8.7482, lng: 115.1672 },
  { code: 'CGK', city: 'Jakarta', country: 'Indonesia', lat: -6.1256, lng: 106.6558 },
  { code: 'MNL', city: 'Manila', country: 'Philippines', lat: 14.5086, lng: 121.0194 },
  { code: 'HAN', city: 'Hanoi', country: 'Vietnam', lat: 21.2187, lng: 105.8042 },
  { code: 'SGN', city: 'Ho Chi Minh City', country: 'Vietnam', lat: 10.8188, lng: 106.6519 },
  { code: 'NRT', city: 'Tokyo', country: 'Japan', lat: 35.7647, lng: 140.3864 },
  { code: 'KIX', city: 'Osaka', country: 'Japan', lat: 34.4347, lng: 135.244 },
  { code: 'ICN', city: 'Seoul', country: 'South Korea', lat: 37.4602, lng: 126.4407 },
  { code: 'PEK', city: 'Beijing', country: 'China', lat: 40.0799, lng: 116.6031 },
  { code: 'PVG', city: 'Shanghai', country: 'China', lat: 31.1443, lng: 121.8083 },
  { code: 'DEL', city: 'New Delhi', country: 'India', lat: 28.5562, lng: 77.1 },
  { code: 'BOM', city: 'Mumbai', country: 'India', lat: 19.0896, lng: 72.8656 },
  { code: 'CMB', city: 'Colombo', country: 'Sri Lanka', lat: 7.1808, lng: 79.8841 },
  { code: 'MLE', city: 'Male', country: 'Maldives', lat: 4.1918, lng: 73.5291 },
  { code: 'KTM', city: 'Kathmandu', country: 'Nepal', lat: 27.6966, lng: 85.3591 },
  { code: 'LHE', city: 'Lahore', country: 'Pakistan', lat: 31.5216, lng: 74.4036 },
  { code: 'KHI', city: 'Karachi', country: 'Pakistan', lat: 24.9065, lng: 67.1608 },
  { code: 'ISB', city: 'Islamabad', country: 'Pakistan', lat: 33.6167, lng: 73.0992 },
  { code: 'DAC', city: 'Dhaka', country: 'Bangladesh', lat: 23.8433, lng: 90.3978 },

  // Americas
  { code: 'JFK', city: 'New York', country: 'USA', lat: 40.6413, lng: -73.7781 },
  { code: 'LAX', city: 'Los Angeles', country: 'USA', lat: 33.9416, lng: -118.4085 },
  { code: 'ORD', city: 'Chicago', country: 'USA', lat: 41.9742, lng: -87.9073 },
  { code: 'MIA', city: 'Miami', country: 'USA', lat: 25.7959, lng: -80.287 },
  { code: 'SFO', city: 'San Francisco', country: 'USA', lat: 37.6213, lng: -122.379 },
  { code: 'LAS', city: 'Las Vegas', country: 'USA', lat: 36.084, lng: -115.1537 },
  { code: 'SEA', city: 'Seattle', country: 'USA', lat: 47.4502, lng: -122.3088 },
  { code: 'BOS', city: 'Boston', country: 'USA', lat: 42.3656, lng: -71.0096 },
  { code: 'ATL', city: 'Atlanta', country: 'USA', lat: 33.6407, lng: -84.4277 },
  { code: 'DFW', city: 'Dallas', country: 'USA', lat: 32.8998, lng: -97.0403 },
  { code: 'YYZ', city: 'Toronto', country: 'Canada', lat: 43.6777, lng: -79.6248 },
  { code: 'YVR', city: 'Vancouver', country: 'Canada', lat: 49.1967, lng: -123.1815 },
  { code: 'MEX', city: 'Mexico City', country: 'Mexico', lat: 19.4363, lng: -99.0721 },
  { code: 'CUN', city: 'Cancun', country: 'Mexico', lat: 21.0365, lng: -86.877 },
  { code: 'GRU', city: 'Sao Paulo', country: 'Brazil', lat: -23.4356, lng: -46.4731 },
  { code: 'GIG', city: 'Rio de Janeiro', country: 'Brazil', lat: -22.8099, lng: -43.2505 },
  { code: 'EZE', city: 'Buenos Aires', country: 'Argentina', lat: -34.8222, lng: -58.5358 },
  { code: 'SCL', city: 'Santiago', country: 'Chile', lat: -33.393, lng: -70.7858 },
  { code: 'BOG', city: 'Bogota', country: 'Colombia', lat: 4.7016, lng: -74.1469 },
  { code: 'LIM', city: 'Lima', country: 'Peru', lat: -12.0219, lng: -77.1143 },

  // Africa
  { code: 'JNB', city: 'Johannesburg', country: 'South Africa', lat: -26.1392, lng: 28.246 },
  { code: 'CPT', city: 'Cape Town', country: 'South Africa', lat: -33.9715, lng: 18.6021 },
  { code: 'NBO', city: 'Nairobi', country: 'Kenya', lat: -1.3192, lng: 36.9278 },
  { code: 'ADD', city: 'Addis Ababa', country: 'Ethiopia', lat: 8.9779, lng: 38.7993 },
  { code: 'CMN', city: 'Casablanca', country: 'Morocco', lat: 33.3675, lng: -7.5898 },
  { code: 'RAK', city: 'Marrakech', country: 'Morocco', lat: 31.6069, lng: -8.0363 },
  { code: 'TUN', city: 'Tunis', country: 'Tunisia', lat: 36.851, lng: 10.2272 },
  { code: 'ALG', city: 'Algiers', country: 'Algeria', lat: 36.691, lng: 3.2154 },
  { code: 'LOS', city: 'Lagos', country: 'Nigeria', lat: 6.5774, lng: 3.3212 },

  // Oceania  
  { code: 'SYD', city: 'Sydney', country: 'Australia', lat: -33.9399, lng: 151.1753 },
  { code: 'MEL', city: 'Melbourne', country: 'Australia', lat: -37.6733, lng: 144.8433 },
  { code: 'BNE', city: 'Brisbane', country: 'Australia', lat: -27.3842, lng: 153.1175 },
  { code: 'PER', city: 'Perth', country: 'Australia', lat: -31.9403, lng: 115.9672 },
  { code: 'AKL', city: 'Auckland', country: 'New Zealand', lat: -37.0082, lng: 174.7917 },
  { code: 'CHC', city: 'Christchurch', country: 'New Zealand', lat: -43.489, lng: 172.5322 },
  { code: 'PPT', city: 'Tahiti', country: 'French Polynesia', lat: -17.5537, lng: -149.6073 },
  { code: 'NAN', city: 'Fiji', country: 'Fiji', lat: -17.7554, lng: 177.4436 },
];

// Arabic translations for cities and countries
const ARABIC_LOCATIONS: Record<string, { city: string; country: string }> = {
  'KWI': { city: 'الكويت', country: 'الكويت' },
  'DXB': { city: 'دبي', country: 'الإمارات' },
  'AUH': { city: 'أبوظبي', country: 'الإمارات' },
  'DOH': { city: 'الدوحة', country: 'قطر' },
  'BAH': { city: 'المنامة', country: 'البحرين' },
  'MCT': { city: 'مسقط', country: 'عُمان' },
  'RUH': { city: 'الرياض', country: 'السعودية' },
  'JED': { city: 'جدة', country: 'السعودية' },
  'DMM': { city: 'الدمام', country: 'السعودية' },
  'MED': { city: 'المدينة المنورة', country: 'السعودية' },
  'AMM': { city: 'عمّان', country: 'الأردن' },
  'BEY': { city: 'بيروت', country: 'لبنان' },
  'TLV': { city: 'تل أبيب', country: 'إسرائيل' },
  'CAI': { city: 'القاهرة', country: 'مصر' },
  'HRG': { city: 'الغردقة', country: 'مصر' },
  'SSH': { city: 'شرم الشيخ', country: 'مصر' },
  'LHR': { city: 'لندن', country: 'المملكة المتحدة' },
  'CDG': { city: 'باريس', country: 'فرنسا' },
  'FCO': { city: 'روما', country: 'إيطاليا' },
  'MXP': { city: 'ميلانو', country: 'إيطاليا' },
  'BCN': { city: 'برشلونة', country: 'إسبانيا' },
  'MAD': { city: 'مدريد', country: 'إسبانيا' },
  'AMS': { city: 'أمستردام', country: 'هولندا' },
  'FRA': { city: 'فرانكفورت', country: 'ألمانيا' },
  'MUC': { city: 'ميونخ', country: 'ألمانيا' },
  'BER': { city: 'برلين', country: 'ألمانيا' },
  'ZRH': { city: 'زيورخ', country: 'سويسرا' },
  'GVA': { city: 'جنيف', country: 'سويسرا' },
  'VIE': { city: 'فيينا', country: 'النمسا' },
  'PRG': { city: 'براغ', country: 'التشيك' },
  'ATH': { city: 'أثينا', country: 'اليونان' },
  'IST': { city: 'إسطنبول', country: 'تركيا' },
  'LIS': { city: 'لشبونة', country: 'البرتغال' },
  'CPH': { city: 'كوبنهاغن', country: 'الدنمارك' },
  'OSL': { city: 'أوسلو', country: 'النرويج' },
  'ARN': { city: 'ستوكهولم', country: 'السويد' },
  'HEL': { city: 'هلسنكي', country: 'فنلندا' },
  'DUB': { city: 'دبلن', country: 'أيرلندا' },
  'BRU': { city: 'بروكسل', country: 'بلجيكا' },
  'WAW': { city: 'وارسو', country: 'بولندا' },
  'SIN': { city: 'سنغافورة', country: 'سنغافورة' },
  'HKG': { city: 'هونغ كونغ', country: 'هونغ كونغ' },
  'BKK': { city: 'بانكوك', country: 'تايلاند' },
  'KUL': { city: 'كوالالمبور', country: 'ماليزيا' },
  'DPS': { city: 'بالي', country: 'إندونيسيا' },
  'CGK': { city: 'جاكرتا', country: 'إندونيسيا' },
  'MNL': { city: 'مانيلا', country: 'الفلبين' },
  'HAN': { city: 'هانوي', country: 'فيتنام' },
  'SGN': { city: 'هو تشي منه', country: 'فيتنام' },
  'NRT': { city: 'طوكيو', country: 'اليابان' },
  'KIX': { city: 'أوساكا', country: 'اليابان' },
  'ICN': { city: 'سيول', country: 'كوريا الجنوبية' },
  'PEK': { city: 'بكين', country: 'الصين' },
  'PVG': { city: 'شنغهاي', country: 'الصين' },
  'DEL': { city: 'نيودلهي', country: 'الهند' },
  'BOM': { city: 'مومباي', country: 'الهند' },
  'CMB': { city: 'كولومبو', country: 'سريلانكا' },
  'MLE': { city: 'ماليه', country: 'المالديف' },
  'KTM': { city: 'كاتماندو', country: 'نيبال' },
  'LHE': { city: 'لاهور', country: 'باكستان' },
  'KHI': { city: 'كراتشي', country: 'باكستان' },
  'ISB': { city: 'إسلام آباد', country: 'باكستان' },
  'DAC': { city: 'دكا', country: 'بنغلاديش' },
  'JFK': { city: 'نيويورك', country: 'أمريكا' },
  'LAX': { city: 'لوس أنجلوس', country: 'أمريكا' },
  'ORD': { city: 'شيكاغو', country: 'أمريكا' },
  'MIA': { city: 'ميامي', country: 'أمريكا' },
  'SFO': { city: 'سان فرانسيسكو', country: 'أمريكا' },
  'LAS': { city: 'لاس فيغاس', country: 'أمريكا' },
  'SEA': { city: 'سياتل', country: 'أمريكا' },
  'BOS': { city: 'بوسطن', country: 'أمريكا' },
  'ATL': { city: 'أتلانتا', country: 'أمريكا' },
  'DFW': { city: 'دالاس', country: 'أمريكا' },
  'YYZ': { city: 'تورنتو', country: 'كندا' },
  'YVR': { city: 'فانكوفر', country: 'كندا' },
  'MEX': { city: 'مكسيكو سيتي', country: 'المكسيك' },
  'CUN': { city: 'كانكون', country: 'المكسيك' },
  'GRU': { city: 'ساو باولو', country: 'البرازيل' },
  'GIG': { city: 'ريو دي جانيرو', country: 'البرازيل' },
  'EZE': { city: 'بوينس آيرس', country: 'الأرجنتين' },
  'SCL': { city: 'سانتياغو', country: 'تشيلي' },
  'BOG': { city: 'بوغوتا', country: 'كولومبيا' },
  'LIM': { city: 'ليما', country: 'بيرو' },
  'JNB': { city: 'جوهانسبرغ', country: 'جنوب أفريقيا' },
  'CPT': { city: 'كيب تاون', country: 'جنوب أفريقيا' },
  'NBO': { city: 'نيروبي', country: 'كينيا' },
  'ADD': { city: 'أديس أبابا', country: 'إثيوبيا' },
  'CMN': { city: 'الدار البيضاء', country: 'المغرب' },
  'RAK': { city: 'مراكش', country: 'المغرب' },
  'TUN': { city: 'تونس', country: 'تونس' },
  'ALG': { city: 'الجزائر', country: 'الجزائر' },
  'LOS': { city: 'لاغوس', country: 'نيجيريا' },
  'SYD': { city: 'سيدني', country: 'أستراليا' },
  'MEL': { city: 'ملبورن', country: 'أستراليا' },
  'BNE': { city: 'بريزبين', country: 'أستراليا' },
  'PER': { city: 'بيرث', country: 'أستراليا' },
  'AKL': { city: 'أوكلاند', country: 'نيوزيلندا' },
  'CHC': { city: 'كرايستشيرش', country: 'نيوزيلندا' },
  'PPT': { city: 'تاهيتي', country: 'بولينيزيا الفرنسية' },
  'NAN': { city: 'فيجي', country: 'فيجي' },
};

// Skeleton Loader Component
const SkeletonCard = () => (
  <div className="rounded-2xl overflow-hidden bg-white shadow-sm border border-gray-100">
    <div className="aspect-[4/3] bg-gray-200 animate-pulse" />
    <div className="p-4 space-y-3">
      <div className="h-6 bg-gray-200 rounded animate-pulse w-3/4" />
      <div className="h-4 bg-gray-200 rounded animate-pulse w-1/2" />
    </div>
  </div>
);

// Calculate distance between two points using Haversine formula
const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Find closest airport to given coordinates
const findClosestAirport = (userLat: number, userLng: number): LocationData => {
  let closestAirport = AIRPORTS_DATABASE[0];
  let minDistance = Infinity;

  for (const airport of AIRPORTS_DATABASE) {
    const distance = calculateDistance(userLat, userLng, airport.lat, airport.lng);
    if (distance < minDistance) {
      minDistance = distance;
      closestAirport = airport;
    }
  }

  return closestAirport;
};

// Helper for Mock Data
const getLocalizedLocations = (lang: 'en' | 'ar') => {
  return AIRPORTS_DATABASE.map(airport => {
    if (lang === 'ar' && ARABIC_LOCATIONS[airport.code]) {
      return {
        code: airport.code,
        city: ARABIC_LOCATIONS[airport.code].city,
        country: ARABIC_LOCATIONS[airport.code].country,
        lat: airport.lat,
        lng: airport.lng,
      };
    }
    return {
      code: airport.code,
      city: airport.city,
      country: airport.country,
      lat: airport.lat,
      lng: airport.lng,
    };
  });
};

const getLocalizedDestinations = (lang: 'en' | 'ar') => {
  if (lang === 'ar') {
    return [
      {
        id: 1,
        slug: 'maldives',
        slugAr: 'افضل-العروض-السياحية-إلي-المالديف-2026',
        name: 'المالديف',
        image: '/assets/images/destinations/maldives_medium.webp',
        imageSmall: '/assets/images/destinations/maldives_small.webp',
        banner: 'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?auto=format&fit=crop&q=80&w=1920',
        article: {
          title: 'أفضل عروض سياحية إلى المالديف 2026 – 2027',
          content: 'المالديف، ملاذ استوائي من الشواطئ النقية، والبحيرات الفيروزية، وأشجار النخيل المتمايلة، غالباً ما توصف بأنها آخر جنة على الأرض. ولكن ما الذي يجعل هذا الأرخبيل آسراً بشكل فريد للمسافرين من جميع أنحاء العالم؟ لا يوجد مكان آخر في العالم يقدم مفهوم "جزيرة واحدة، منتجع واحد" تماماً مثل المالديف. تضمن هذه الجغرافيا الفريدة مستوى من الخصوصية والحصرية يصعب العثور عليه في مكان آخر. سواء اخترت فيلا فوق الماء مع وصول مباشر للمحيط أو فيلا شاطئية محاطة بالنباتات المورقة، فأنت مضمون بملاذ من السلام. تحت السطح المتلألئ يكمن عالم نابض بالحياة من الشعاب المرجانية التي تعج بالحياة.',
          quote: '"المالديف ليست مجرد وجهة؛ إنها شعور. إنها شعور الرمل الدافئ بين أصابع قدميك وصوت الأمواج اللطيفة التي تهدئك للنوم."'
        }
      },
      {
        id: 2,
        slug: 'paris',
        slugAr: 'افضل-العروض-السياحية-إلي-باريس-2026',
        name: 'باريس',
        image: '/assets/images/destinations/paris_medium.webp',
        imageSmall: '/assets/images/destinations/paris_small.webp',
        banner: 'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?auto=format&fit=crop&q=80&w=1920',
        article: {
          title: 'أفضل عروض سياحية إلى باريس 2026 – 2027',
          content: 'باريس، عاصمة الأناقة والفن والثقافة، تستقبل ملايين الزوار سنوياً بسحرها الفريد. من برج إيفل الشامخ إلى متحف اللوفر العريق، تقدم باريس تجربة لا تُنسى. استمتع بنزهة على ضفاف نهر السين، واكتشف أحياء مونمارتر الساحرة، وتذوق أشهى المأكولات الفرنسية في المقاهي التاريخية. باريس ليست مجرد مدينة، بل هي تجربة حياة كاملة.',
          quote: '"باريس دائماً فكرة جيدة - تذهب إليها شخصاً وتعود منها آخر."'
        }
      },
      {
        id: 3,
        slug: 'switzerland',
        slugAr: 'افضل-العروض-السياحية-إلي-سويسرا-2026',
        name: 'سويسرا',
        image: '/assets/images/destinations/switzerland_medium.webp',
        imageSmall: '/assets/images/destinations/switzerland_small.webp',
        banner: 'https://images.unsplash.com/photo-1530122037265-a5f1f91d3b99?auto=format&fit=crop&q=80&w=1920',
        article: {
          title: 'أفضل عروض سياحية إلى سويسرا 2026 – 2027',
          content: 'سويسرا، جوهرة جبال الألب، تأسر القلوب بمناظرها الطبيعية الخلابة وقراها الساحرة. من قمم الجبال المكسوة بالثلوج إلى البحيرات الصافية كالكريستال، تقدم سويسرا مزيجاً فريداً من المغامرة والاسترخاء. استكشف مدن زيورخ وجنيف العالمية، وتمتع برحلات القطارات البانورامية الشهيرة، واستمتع بأجود أنواع الشوكولاتة والجبن.',
          quote: '"سويسرا هي المكان الذي تلتقي فيه السماء بالأرض في انسجام تام."'
        }
      },
      {
        id: 4,
        slug: 'kyoto',
        slugAr: 'افضل-العروض-السياحية-إلي-كيوتو-2026',
        name: 'كيوتو',
        image: '/assets/images/destinations/kyoto_medium.webp',
        imageSmall: '/assets/images/destinations/kyoto_small.webp',
        banner: 'https://images.unsplash.com/photo-1545569341-9eb8b30979d9?auto=format&fit=crop&q=80&w=1920',
        article: {
          title: 'أفضل عروض سياحية إلى كيوتو 2026 – 2027',
          content: 'كيوتو، العاصمة الثقافية لليابان، تحافظ على تراث الساموراي والجيشا حتى اليوم. بمعابدها البوذية العريقة وحدائقها الشهيرة وأزقتها التاريخية، تقدم كيوتو رحلة عبر الزمن. شاهد أزهار الكرز في الربيع أو أوراق الخريف الذهبية، وجرب حفل الشاي التقليدي، واستمتع بالمطبخ الياباني الأصيل.',
          quote: '"في كيوتو، الماضي والحاضر يتعايشان في تناغم ساحر."'
        }
      },
      {
        id: 5,
        slug: 'bali',
        slugAr: 'افضل-العروض-السياحية-إلي-بالي-2026',
        name: 'بالي',
        image: '/assets/images/destinations/bali_medium.webp',
        imageSmall: '/assets/images/destinations/bali_small.webp',
        banner: 'https://images.unsplash.com/photo-1573790387438-4da905039392?auto=format&fit=crop&q=80&w=1920',
        article: {
          title: 'أفضل عروض سياحية إلى بالي 2026 – 2027',
          content: 'بالي، جزيرة الآلهة، تجمع بين الشواطئ الذهبية والمعابد المقدسة ومدرجات الأرز الخضراء. استمتع بركوب الأمواج في سيمينياك، واستكشف معابد أوبود الروحانية، وشاهد رقصات الكيتشاك التقليدية عند غروب الشمس. بالي وجهة مثالية للباحثين عن الاسترخاء والمغامرة على حد سواء.',
          quote: '"بالي ليست مجرد جزيرة، إنها حالة ذهنية تغير حياتك."'
        }
      },
      {
        id: 6,
        slug: 'singapore',
        slugAr: 'افضل-العروض-السياحية-إلي-سنغافورة-2026',
        name: 'سنغافورة',
        image: '/assets/images/destinations/singapore_medium.webp',
        imageSmall: '/assets/images/destinations/singapore_small.webp',
        banner: 'https://images.unsplash.com/photo-1508964942454-1a56651d54ac?auto=format&fit=crop&q=80&w=1920',
        article: {
          title: 'أفضل عروض سياحية إلى سنغافورة 2026 – 2027',
          content: 'سنغافورة، المدينة الحديثة التي تمزج بين التراث الآسيوي والحداثة العالمية. اكتشف حدائق الخليج المستقبلية، وتسوق في أورتشارد رود الشهير، واستمتع بالمأكولات المتنوعة من هوكر سنترز التقليدية. مارينا باي ساندز وجزيرة سنتوسا وحي الصين التاريخي كلها تنتظرك لاستكشافها.',
          quote: '"سنغافورة مدينة صغيرة المساحة، كبيرة الأحلام."'
        }
      },
      {
        id: 7,
        slug: 'thailand',
        slugAr: 'افضل-العروض-السياحية-إلي-تايلاند-2026',
        name: 'تايلاند',
        image: '/assets/images/destinations/thailand_medium.webp',
        imageSmall: '/assets/images/destinations/thailand_small.webp',
        banner: 'https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?auto=format&fit=crop&q=80&w=1920',
        article: {
          title: 'أفضل عروض سياحية إلى تايلاند 2026 – 2027',
          content: 'تايلاند، أرض الابتسامات، تجمع بين الشواطئ الاستوائية الخلابة والمعابد البوذية العريقة والمأكولات الشهية. من بانكوك النابضة بالحياة إلى جزر بوكيت وكرابي الساحرة، تقدم تايلاند تجربة سياحية لا مثيل لها. استمتع بالضيافة الآسيوية الدافئة واكتشف كنوز هذا البلد الساحر.',
          quote: '"تايلاند تأسرك بسحرها من أول لحظة وتبقى في قلبك للأبد."'
        }
      },
    ];
  }
  return [
    {
      id: 1,
      slug: 'maldives',
      name: 'Maldives',
      image: '/assets/images/destinations/maldives_medium.webp',
      imageSmall: '/assets/images/destinations/maldives_small.webp',
      banner: 'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?auto=format&fit=crop&q=80&w=1920',
      article: {
        title: 'Best Maldives Travel Offers 2026 – 2027',
        content: 'The Maldives, a tropical haven of immaculate beaches, turquoise lagoons, and swaying palm trees, is often described as the last paradise on earth. Nowhere else in the world offers the concept of "one island, one resort" quite like the Maldives. This unique geography ensures a level of privacy and exclusivity that is hard to find elsewhere. Beneath the shimmering surface lies a vibrant world of coral reefs teeming with life.',
        quote: '"The Maldives is not just a destination; it\'s an emotion."'
      }
    },
    {
      id: 2,
      slug: 'paris',
      name: 'Paris',
      image: '/assets/images/destinations/paris_medium.webp',
      imageSmall: '/assets/images/destinations/paris_small.webp',
      banner: 'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?auto=format&fit=crop&q=80&w=1920',
      article: {
        title: 'Best Paris Travel Offers 2026 – 2027',
        content: 'Paris, the capital of elegance, art, and culture, welcomes millions of visitors annually with its unique charm. From the iconic Eiffel Tower to the renowned Louvre Museum, Paris offers an unforgettable experience. Enjoy a stroll along the banks of the Seine, discover the charming Montmartre neighborhoods, and taste the finest French cuisine in historic cafes.',
        quote: '"Paris is always a good idea."'
      }
    },
    {
      id: 3,
      slug: 'switzerland',
      name: 'Switzerland',
      image: '/assets/images/destinations/switzerland_medium.webp',
      imageSmall: '/assets/images/destinations/switzerland_small.webp',
      banner: 'https://images.unsplash.com/photo-1530122037265-a5f1f91d3b99?auto=format&fit=crop&q=80&w=1920',
      article: {
        title: 'Best Switzerland Travel Offers 2026 – 2027',
        content: 'Switzerland, the jewel of the Alps, captivates hearts with its breathtaking natural scenery and charming villages. From snow-capped mountain peaks to crystal-clear lakes, Switzerland offers a unique blend of adventure and relaxation. Explore the global cities of Zurich and Geneva, enjoy famous panoramic train journeys, and savor the finest chocolate and cheese.',
        quote: '"Switzerland is where the sky meets the earth in perfect harmony."'
      }
    },
    {
      id: 4,
      slug: 'kyoto',
      name: 'Kyoto',
      image: '/assets/images/destinations/kyoto_medium.webp',
      imageSmall: '/assets/images/destinations/kyoto_small.webp',
      banner: 'https://images.unsplash.com/photo-1545569341-9eb8b30979d9?auto=format&fit=crop&q=80&w=1920',
      article: {
        title: 'Best Kyoto Travel Offers 2026 – 2027',
        content: 'Kyoto, Japan\'s cultural capital, preserves the heritage of samurai and geisha to this day. With its ancient Buddhist temples, famous gardens, and historic alleys, Kyoto offers a journey through time. See cherry blossoms in spring or golden autumn leaves, experience a traditional tea ceremony, and enjoy authentic Japanese cuisine.',
        quote: '"In Kyoto, past and present coexist in enchanting harmony."'
      }
    },
    {
      id: 5,
      slug: 'bali',
      name: 'Bali',
      image: '/assets/images/destinations/bali_medium.webp',
      imageSmall: '/assets/images/destinations/bali_small.webp',
      banner: 'https://images.unsplash.com/photo-1573790387438-4da905039392?auto=format&fit=crop&q=80&w=1920',
      article: {
        title: 'Best Bali Travel Offers 2026 – 2027',
        content: 'Bali, the Island of Gods, combines golden beaches, sacred temples, and lush green rice terraces. Enjoy surfing in Seminyak, explore Ubud\'s spiritual temples, and watch traditional Kecak dances at sunset. Bali is the perfect destination for those seeking relaxation and adventure alike.',
        quote: '"Bali is not just an island, it\'s a state of mind that changes your life."'
      }
    },
    {
      id: 6,
      slug: 'singapore',
      name: 'Singapore',
      image: '/assets/images/destinations/singapore_medium.webp',
      imageSmall: '/assets/images/destinations/singapore_small.webp',
      banner: 'https://images.unsplash.com/photo-1508964942454-1a56651d54ac?auto=format&fit=crop&q=80&w=1920',
      article: {
        title: 'Best Singapore Travel Offers 2026 – 2027',
        content: 'Singapore, the modern city-state that blends Asian heritage with global modernity. Discover the futuristic Gardens by the Bay, shop on famous Orchard Road, and enjoy diverse cuisines from traditional hawker centers. Marina Bay Sands, Sentosa Island, and the historic Chinatown all await your exploration.',
        quote: '"Singapore is a small city with big dreams."'
      }
    },
    {
      id: 7,
      slug: 'thailand',
      name: 'Thailand',
      image: '/assets/images/destinations/thailand_medium.webp',
      imageSmall: '/assets/images/destinations/thailand_small.webp',
      banner: 'https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?auto=format&fit=crop&q=80&w=1920',
      article: {
        title: 'Best Thailand Travel Offers 2026 – 2027',
        content: 'Thailand, the Land of Smiles, combines stunning tropical beaches, ancient Buddhist temples, and delicious cuisine. From vibrant Bangkok to the charming islands of Phuket and Krabi, Thailand offers an unparalleled travel experience. Enjoy warm Asian hospitality and discover the treasures of this enchanting country.',
        quote: '"Thailand captivates you from the first moment and stays in your heart forever."'
      }
    },
  ];
};


import { getLocalizedPackages, getLocalizedMaldivesPackages } from './mockData';

const getLocalizedTestimonials = (lang: 'en' | 'ar') => {
  if (lang === 'ar') {
    return [
      {
        id: 1,
        name: "سارة جونسون",
        role: "عاشقة للسفر",
        text: "كانت رحلة المالديف التي نظمتها COMO ساحرة للغاية. تم الاهتمام بكل التفاصيل!",
        image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=100&h=100"
      },
      {
        id: 2,
        name: "أحمد السيد",
        role: "مسافر أعمال",
        text: "خدمة احترافية وأفضل الأسعار التي وجدتها. توصية الفيلا فوق الماء كانت مثالية.",
        image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=100&h=100"
      },
      {
        id: 3,
        name: "إميلي تشن",
        role: "مصورة",
        text: "حلم المصور. كانت اللوجستيات سلسة، مما سمح لي بالتركيز على التقاط جمال الجزر.",
        image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80&w=100&h=100"
      }
    ];
  }
  return [
    {
      id: 1,
      name: "Sarah Johnson",
      role: "Travel Enthusiast",
      text: "The Maldives trip organized by COMO was absolutely breathtaking. Every detail was taken care of!",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=100&h=100"
    },
    {
      id: 2,
      name: "Ahmed Al-Sayed",
      role: "Business Traveler",
      text: "Professional service and the best rates I could find. The overwater villa recommendation was perfect.",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=100&h=100"
    },
    {
      id: 3,
      name: "Emily Chen",
      role: "Photographer",
      text: "A photographer's dream. The logistics were seamless, allowing me to focus on capturing the beauty of the islands.",
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80&w=100&h=100"
    }
  ];
};

const FLEXIBILITY_OPTIONS = [
  { label: 'exact', value: 'exact' },
  { label: '+ day after', value: '+1_after' },
  { label: '+ day before', value: '+1_before' },
  { label: '± 1 day', value: 'pm_1' },
  { label: '± 2 days', value: 'pm_2' },
  { label: '± 3 days', value: 'pm_3' },
];

// --- Utilities ---

// Portal extracted to Portal.tsx

const useDropdownPosition = (ref: React.RefObject<HTMLElement | null>, isOpen: boolean, options: { matchWidth?: boolean } = {}) => {
  const { matchWidth = true } = options;
  const [style, setStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    if (isOpen && ref.current) {
      const updatePosition = () => {
        const rect = ref.current!.getBoundingClientRect();
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;

        const isRightSide = rect.left > window.innerWidth / 2;

        const newStyle: React.CSSProperties = {
          position: 'absolute',
          top: `${rect.bottom + scrollY + 8}px`,
          zIndex: 9999,
        };

        if (matchWidth) {
          newStyle.width = `${rect.width}px`;
          newStyle.left = `${rect.left + scrollX}px`;
        } else {
          if (isRightSide) {
            newStyle.left = `${rect.right + scrollX}px`;
            newStyle.transform = 'translateX(-100%)';
          } else {
            newStyle.left = `${rect.left + scrollX}px`;
          }
          newStyle.maxWidth = '94vw';
        }

        setStyle(newStyle);
      };

      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }
  }, [isOpen, ref, matchWidth]);

  return style;
};

// --- Helper Components ---

const AutocompleteInput = ({
  label,
  value,
  onChange,
  icon: Icon,
  placeholder,
  locations,
  autoFocus = false
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  icon: React.ElementType;
  placeholder: string;
  locations: any[];
  autoFocus?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  const dropdownStyle = useDropdownPosition(inputRef, isOpen, { matchWidth: true });

  // Sync inputValue with external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        // Reset to the last valid value if user typed but didn't select
        setInputValue(value);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (autoFocus && textInputRef.current) {
      textInputRef.current.focus();
    }
  }, [autoFocus]);

  return (
    <div ref={inputRef} className="relative flex-1 w-full rounded-[0.6rem] bg-gray-50 border border-gray-100 p-2.5 transition-all focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:bg-white group">
      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block flex items-center gap-1.5">
        <Icon className="w-3 h-3 text-gray-400" /> {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          ref={textInputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            // Only open dropdown if no valid selection (doesn't end with airport code pattern)
            const hasValidSelection = /\([A-Z]{3}\)$/.test(inputValue);
            if (!hasValidSelection) {
              setIsOpen(true);
            }
          }}
          className="bg-transparent w-full outline-none text-base md:text-lg font-bold text-gray-900 placeholder-gray-300 truncate text-start h-8"
          placeholder={placeholder}
        />
        {inputValue && (
          <button onClick={() => { setInputValue(''); onChange(''); }} className="text-gray-400 hover:text-gray-600 p-1" aria-label="Clear input">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {isOpen && (
        <Portal>
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="bg-white rounded-[0.6rem] shadow-[0_10px_40px_-10px_rgba(0,0,0,0.2)] border border-gray-100 p-3 max-h-[400px] overflow-y-auto no-scrollbar animate-in fade-in zoom-in-95 duration-150 min-w-[400px]"
          >
            {(() => {
              const searchValue = inputValue.toLowerCase().trim();

              // Popular destinations to show when no search - prioritize popular travel destinations
              const popularCodes = ['DXB', 'IST', 'LHR', 'CDG', 'BCN', 'MLE', 'BKK', 'SIN', 'FCO', 'AMS', 'MUC', 'ZRH', 'VIE', 'ATH', 'PRG', 'DPS'];

              let results = locations;

              if (searchValue) {
                // Filter and sort by relevance
                results = locations
                  .filter(l =>
                    l.city.toLowerCase().includes(searchValue) ||
                    l.code.toLowerCase().includes(searchValue) ||
                    l.country.toLowerCase().includes(searchValue)
                  )
                  .sort((a, b) => {
                    const aCity = a.city.toLowerCase();
                    const bCity = b.city.toLowerCase();
                    if (aCity.startsWith(searchValue) && !bCity.startsWith(searchValue)) return -1;
                    if (!aCity.startsWith(searchValue) && bCity.startsWith(searchValue)) return 1;
                    return aCity.localeCompare(bCity);
                  });
              } else {
                // Show popular destinations first when no search
                results = [
                  ...locations.filter(l => popularCodes.includes(l.code)),
                  ...locations.filter(l => !popularCodes.includes(l.code))
                ].slice(0, 16);
              }

              const displayResults = results.slice(0, 16);

              return displayResults.length > 0 ? (
                <div className="grid grid-cols-4 gap-1">
                  {displayResults.map((loc) => (
                    <div
                      key={loc.code}
                      className="p-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-all text-center group"
                      onClick={() => {
                        const selectedValue = `${loc.city}, ${loc.country} (${loc.code})`;
                        setInputValue(selectedValue);
                        onChange(selectedValue);
                        setIsOpen(false);
                      }}
                    >
                      <div className="text-xs font-bold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                        {loc.city}
                      </div>
                      <div className="text-[10px] text-gray-400 truncate">
                        {loc.code}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-sm text-gray-500 text-center">No locations found</div>
              );
            })()}
          </div>
        </Portal>
      )}
    </div>
  );
};

// Destination Autocomplete for Packages Page Filter
const DestinationAutocomplete = ({
  lang,
  destinations,
  selectedDestination,
  onSelect
}: {
  lang: 'en' | 'ar';
  destinations: Destination[];
  selectedDestination: number | null;
  onSelect: (destId: number | null) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const inputRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const dropdownStyle = useDropdownPosition(inputRef, isOpen, { matchWidth: true });

  // Get the selected destination name
  const selectedName = selectedDestination !== null
    ? destinations.find(d => d.id === selectedDestination)?.name || ''
    : '';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchValue('');
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter destinations based on search
  const filteredDestinations = searchValue.trim()
    ? destinations.filter(d =>
      d.name.toLowerCase().includes(searchValue.toLowerCase())
    )
    : destinations;

  return (
    <div ref={inputRef} className="relative">
      <div
        className="bg-white rounded-xl border-2 border-gray-100 p-3.5 cursor-pointer hover:border-emerald-200 transition-all shadow-sm flex items-center justify-between"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-3 flex-1">
          <MapPin className="w-5 h-5 text-emerald-600" />
          {isOpen ? (
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder={lang === 'ar' ? 'ابحث عن وجهة...' : 'Search destination...'}
              className="flex-1 outline-none bg-transparent text-gray-900 font-medium placeholder-gray-400"
              autoFocus
            />
          ) : (
            <span className={`font-medium ${selectedName ? 'text-gray-900' : 'text-gray-400'}`}>
              {selectedName || (lang === 'ar' ? 'جميع الوجهات' : 'All Destinations')}
            </span>
          )}
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {isOpen && (
        <Portal>
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="bg-white rounded-xl shadow-xl border border-gray-100 py-2 max-h-[300px] overflow-y-auto animate-in fade-in zoom-in-95 duration-150 z-50"
          >
            {/* All Destinations option */}
            <div
              className={`px-4 py-3 cursor-pointer transition-all flex items-center gap-3 ${selectedDestination === null
                ? 'bg-emerald-50 text-emerald-700'
                : 'hover:bg-gray-50 text-gray-700'
                }`}
              onClick={() => {
                onSelect(null);
                setIsOpen(false);
                setSearchValue('');
              }}
            >
              <Globe className="w-4 h-4" />
              <span className="font-medium">{lang === 'ar' ? 'جميع الوجهات' : 'All Destinations'}</span>
            </div>

            {filteredDestinations.length > 0 ? (
              filteredDestinations.map((dest) => (
                <div
                  key={dest.id}
                  className={`px-4 py-3 cursor-pointer transition-all flex items-center gap-3 ${selectedDestination === dest.id
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  onClick={() => {
                    onSelect(dest.id);
                    setIsOpen(false);
                    setSearchValue('');
                  }}
                >
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">{dest.name}</span>
                </div>
              ))
            ) : (
              <div className="px-4 py-3 text-gray-400 text-sm text-center">
                {lang === 'ar' ? 'لا توجد نتائج' : 'No results found'}
              </div>
            )}
          </div>
        </Portal>
      )}
    </div>
  );
};

const RangeDatePicker = ({
  label,
  startDate,
  endDate,
  onDateChange,
  placeholder,
  lang,
  tWiz,
  tripType = 'roundtrip'
}: {
  label: string;
  startDate: Date | null;
  endDate: Date | null;
  onDateChange: (start: Date | null, end: Date | null) => void;
  placeholder: string;
  lang: 'en' | 'ar';
  tWiz: any;
  tripType?: 'oneway' | 'roundtrip';
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(new Date());
  const [activeMode, setActiveMode] = useState<'dates' | 'weekend' | 'month'>('dates');

  const inputRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const dropdownStyle = useDropdownPosition(inputRef, isOpen, { matchWidth: false });

  const [flexibility, setFlexibility] = useState('exact');
  const [showFlexDropdown, setShowFlexDropdown] = useState(false);

  // Use Gregorian calendar explicitly for Arabic locale (ar-u-ca-gregory)
  const locale = lang === 'ar' ? 'ar-u-ca-gregory' : 'en-US';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setShowFlexDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatDate = (date: Date | null) => {
    if (!date) return '';
    return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  };

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const generateCalendar = (year: number, month: number) => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = new Date(year, month, 1).getDay();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));

    while (days.length < 42) {
      days.push(null);
    }

    return days;
  };

  const isSelected = (date: Date) => {
    if (!date) return false;
    if (startDate && date.getTime() === startDate.getTime()) return true;
    if (tripType === 'roundtrip' && endDate && date.getTime() === endDate.getTime()) return true;
    if (tripType === 'roundtrip' && startDate && endDate && date > startDate && date < endDate) return true;
    return false;
  };

  const isRangeStart = (date: Date) => startDate && date.getTime() === startDate.getTime();
  const isRangeEnd = (date: Date) => tripType === 'roundtrip' && endDate && date.getTime() === endDate.getTime();
  const isInRange = (date: Date) => tripType === 'roundtrip' && startDate && endDate && date > startDate && date < endDate;

  const handleDateClick = (date: Date) => {
    setActiveMode('dates');

    if (tripType === 'oneway') {
      // One-way: just select departure date and close
      onDateChange(date, null);
      setIsOpen(false);
    } else {
      // Two-way: select range
      if (!startDate || (startDate && endDate)) {
        // Starting fresh selection or reselecting - stay open for return date
        onDateChange(date, null);
      } else {
        // Selecting return date - close after selection
        if (date < startDate) {
          onDateChange(date, startDate);
        } else {
          onDateChange(startDate, date);
        }
        setIsOpen(false);
      }
    }
  };

  const selectNextWeekend = () => {
    // Middle Eastern weekend: Thursday-Friday
    const today = new Date();
    const nextThursday = new Date();
    // Thursday is day 4 in JavaScript (0=Sunday)
    const daysUntilThursday = (4 - today.getDay() + 7) % 7 || 7;
    nextThursday.setDate(today.getDate() + daysUntilThursday);
    const nextFriday = new Date(nextThursday);
    nextFriday.setDate(nextThursday.getDate() + 1);
    onDateChange(nextThursday, nextFriday);
    setViewDate(nextThursday);
    setActiveMode('weekend');
  };

  const selectThisMonth = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Start from today (not past dates), end at last day of month
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    onDateChange(today, lastDay);
    setViewDate(today);
    setActiveMode('month');
  };

  const nextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };
  const prevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const renderMonth = (offset: number) => {
    const targetDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + offset, 1);
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();
    const days = generateCalendar(year, month);
    const monthName = targetDate.toLocaleString(locale, { month: 'long' });

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
      <div className="flex-1 min-w-[300px]">
        <h3 className="text-center font-bold text-gray-900 mb-4 select-none">{monthName} {year}</h3>
        <div className="grid grid-cols-7 gap-1 text-center mb-2">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(d => (
            <div key={d} className="text-xs font-bold text-gray-400 h-8 flex items-center justify-center select-none">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-y-1">
          {days.map((date, idx) => {
            if (!date) return <div key={idx} className="h-10" />;

            const selected = isSelected(date);
            const start = isRangeStart(date);
            const end = isRangeEnd(date);
            const range = isInRange(date);
            const isDisabled = date < today;

            let bgClass = "";
            let textClass = "text-gray-700 hover:bg-gray-100";
            let roundedClass = "rounded-full";

            if (isDisabled) {
              textClass = "text-gray-300 cursor-not-allowed";
            } else if (selected) {
              textClass = "text-white font-bold";
              if (start) {
                bgClass = "bg-[#D4AF37]";
                roundedClass = endDate ? "rounded-l-full" : "rounded-full";
              } else if (end) {
                bgClass = "bg-[#D4AF37]";
                roundedClass = "rounded-r-full";
              } else if (range) {
                bgClass = "bg-gray-100";
                textClass = "text-gray-900";
                roundedClass = "";
              }
            }

            return (
              <button
                key={idx}
                disabled={isDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDateClick(date);
                }}
                className={`h-10 w-full flex items-center justify-center text-sm relative ${range && !isDisabled ? 'bg-gray-100' : ''}`}
              >
                <div className={`w-9 h-9 flex items-center justify-center ${roundedClass} ${bgClass} ${start || end ? 'shadow-md scale-105' : ''} ${textClass} transition-all`}>
                  {date.getDate()}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const displayValue = startDate
    ? `${formatDate(startDate)}${endDate ? ` - ${formatDate(endDate)}` : ''}`
    : '';

  return (
    <div ref={inputRef} className="relative w-full rounded-[0.6rem] bg-gray-50 border border-gray-100 p-2.5 transition-all focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:bg-white group cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block flex items-center gap-1.5">
        <CalendarIcon className="w-3 h-3 text-gray-400" /> {label}
      </label>
      <div
        className="flex items-center gap-2"
      >
        <input
          type="text"
          readOnly
          value={displayValue}
          className="bg-transparent w-full outline-none text-base md:text-lg font-bold text-gray-900 placeholder-gray-300 cursor-pointer text-start h-8"
          placeholder={placeholder}
        />
      </div>

      {isOpen && (
        <Portal>
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            onMouseDown={(e) => e.stopPropagation()}
            className="bg-white rounded-[0.6rem] shadow-[0_20px_50px_-12px_rgba(0,0,0,0.3)] border border-gray-100 p-6 w-[340px] md:w-[850px] overflow-hidden animate-in fade-in duration-200"
          >
            {/* Header / Tabs - Only show for roundtrip */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-gray-100 pb-4 mb-6 gap-4">
              {tripType === 'roundtrip' && (
                <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
                  <button
                    onClick={(e) => { e.stopPropagation(); setActiveMode('dates'); }}
                    className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${activeMode === 'dates' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-900'}`}
                  >
                    {tWiz.dates}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); selectNextWeekend(); }}
                    className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${activeMode === 'weekend' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-900'}`}
                  >
                    {tWiz.weekend}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); selectThisMonth(); }}
                    className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${activeMode === 'month' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-900'}`}
                  >
                    {tWiz.month}
                  </button>
                </div>
              )}

              <div className="relative flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-500">Departure</span>
                  <button
                    onClick={() => setShowFlexDropdown(!showFlexDropdown)}
                    className="flex items-center gap-1 bg-blue-50 px-2.5 py-1 rounded-md text-xs font-bold text-blue-600 hover:bg-blue-100 transition-colors"
                  >
                    {FLEXIBILITY_OPTIONS.find(o => o.value === flexibility)?.label} <ChevronDown className="w-3 h-3" />
                  </button>
                </div>

                {showFlexDropdown && (
                  <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-2 w-48 z-50 overflow-hidden">
                    {FLEXIBILITY_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 text-gray-700 flex items-center justify-between group"
                        onClick={() => {
                          setFlexibility(opt.value);
                          setShowFlexDropdown(false);
                        }}
                      >
                        {opt.label}
                        {flexibility === opt.value && <Check className="w-3.5 h-3.5 text-blue-600" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Calendar Navigation */}
            <div className="relative">
              <button onClick={(e) => { e.stopPropagation(); prevMonth(); }} className="absolute left-0 top-0 p-2 hover:bg-gray-100 rounded-full z-10 -ml-2 text-gray-400 hover:text-gray-900 transition-colors rtl:right-0 rtl:left-auto ltr:left-0 ltr:right-auto">
                <ChevronLeft className="w-5 h-5 rtl:rotate-180" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); nextMonth(); }} className="absolute right-0 top-0 p-2 hover:bg-gray-100 rounded-full z-10 -mr-2 text-gray-400 hover:text-gray-900 transition-colors rtl:left-0 rtl:right-auto ltr:right-0 ltr:left-auto">
                <ChevronRight className="w-5 h-5 rtl:rotate-180" />
              </button>

              {/* Calendars */}
              <div className="flex flex-col md:flex-row gap-8 overflow-x-auto no-scrollbar pb-2">
                {renderMonth(0)}
                <div className="hidden md:block border-l rtl:border-r rtl:border-l-0 border-gray-100 pl-8 rtl:pr-8 rtl:pl-0">{renderMonth(1)}</div>
              </div>
            </div>


          </div>
        </Portal>
      )}
    </div>
  );
};

// --- Main Components ---

// Navbar extracted to Navbar.tsx

const RatingsBanner = () => (
  <div className="w-full max-w-4xl mx-auto mt-12 md:mt-16 px-4 grid grid-cols-1 md:grid-cols-3 gap-6 items-center justify-items-center bg-gray-50 backdrop-blur-md rounded-xl p-6 border-2 border-white relative z-40">
    {/* Google */}
    <div className="flex flex-col items-center gap-2">
      <img src="https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg" alt="Google" className="h-6" width="74" height="24" />
      <div className="flex items-center gap-1.5">
        <div className="flex text-[#F4B400] gap-0.5">
          {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 fill-current" />)}
        </div>
        <span className="font-bold text-gray-900 text-sm">4.8/5 Rating</span>
      </div>
    </div>
    {/* Clutch */}
    <div className="flex flex-col items-center gap-2 w-full py-4 md:py-0">
      <div className="flex items-center">
        <span className="font-bold text-2xl text-[#182a36] tracking-tight">Clutch</span>
        <div className="w-2 h-2 bg-[#ff3d2e] rounded-full ml-0.5 mt-1.5"></div>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="flex text-[#F4B400] gap-0.5">
          {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 fill-current" />)}
        </div>
        <span className="font-bold text-gray-900 text-sm">4.8/5 Rating</span>
      </div>
    </div>
    {/* Trustpilot */}
    <div className="flex flex-col items-center gap-2">
      <div className="flex items-center gap-1.5">
        <Star className="w-6 h-6 text-[#00b67a] fill-[#00b67a]" />
        <span className="font-bold text-xl text-[#1c1c1c]">Trustpilot</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="flex bg-[#00b67a] gap-0.5 px-1 py-0.5">
          {[1, 2, 3, 4, 5].map(i => <div key={i} className="bg-white/20 p-0.5"><Star className="w-3 h-3 fill-white text-white" /></div>)}
        </div>
        <span className="font-bold text-gray-900 text-sm">4.8/5 Rating</span>
      </div>
    </div>
  </div>
);

const BookingWidget = ({ lang }: { lang: 'en' | 'ar' }) => {
  const currentLang = TRANSLATIONS[lang] ? lang : 'en';
  const t = TRANSLATIONS[currentLang].hero;
  const tWiz = TRANSLATIONS[currentLang].hero.wizard;
  const [activeTab, setActiveTab] = useState<'flights' | 'hotel' | 'trains'>('flights');
  const [step, setStep] = useState(0);

  // State for form
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [tripType, setTripType] = useState<'oneway' | 'roundtrip'>('roundtrip');
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);

  // Flights guest state
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);

  // Hotel guest state
  const [rooms, setRooms] = useState([{ id: 1, adults: 2, children: 0 }]);

  const locations = getLocalizedLocations(lang);

  // Default to Kuwait - no automatic geolocation request to avoid permission prompt on page load
  useEffect(() => {
    const defaultAirport = locations.find(l => l.code === 'KWI');
    if (defaultAirport && !origin && activeTab === 'flights') {
      setOrigin(`${defaultAirport.city}, ${defaultAirport.country} (${defaultAirport.code})`);
    } else if (activeTab === 'trains') {
      setOrigin('');
    }
  }, [lang, activeTab]);

  const nextStep = () => {
    setStep(prev => Math.min(prev + 1, 2));
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 0));
  };

  const handleBook = () => {
    const formatDate = (date: Date | null) => {
      if (!date) return 'غير محدد';
      // Use en-GB to ensure Gregorian calendar (DD/MM/YYYY)
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    let message = '';

    if (activeTab === 'flights') {
      message = `🛫 *طلب حجز طيران جديد*

`;
      message += `✈️ *نوع الرحلة:* ${tripType === 'oneway' ? 'ذهاب فقط' : 'ذهاب وعودة'}\n`;
      message += `📍 *من:* ${origin}\n`;
      message += `📍 *إلى:* ${destination}\n`;
      message += `🗓️ *تاريخ المغادرة:* ${formatDate(startDate)}\n`;
      if (tripType === 'roundtrip' && endDate) {
        message += `🗓️ *تاريخ العودة:* ${formatDate(endDate)}\n`;
      }
      message += `👥 *عدد البالغين:* ${adults}\n`;
      message += `👶 *عدد الأطفال:* ${children}\n`;
      message += `👥 *عدد البالغين:* ${adults}\n`;
      message += `👶 *عدد الأطفال:* ${children}\n`;
      message += `\n💬 أرجو التواصل معي لإتمام الحجز. شكراً!`;
    } else if (activeTab === 'trains') {
      message = `🚂 *طلب حجز قطار جديد*
      
`;
      message += `🛤️ *نوع الرحلة:* ${tripType === 'oneway' ? 'ذهاب فقط' : 'ذهاب وعودة'}\n`;
      message += `📍 *من:* ${origin}\n`;
      message += `📍 *إلى:* ${destination}\n`;
      message += `🗓️ *تاريخ المغادرة:* ${formatDate(startDate)}\n`;
      if (tripType === 'roundtrip' && endDate) {
        message += `🗓️ *تاريخ العودة:* ${formatDate(endDate)}\n`;
      }
      message += `👥 *عدد البالغين:* ${adults}\n`;
      message += `👶 *عدد الأطفال:* ${children}\n`;
      message += `\n💬 أرجو التواصل معي لإتمام الحجز. شكراً!`;
    } else {
      // Hotel booking
      const totalAdults = rooms.reduce((sum, room) => sum + room.adults, 0);
      const totalChildren = rooms.reduce((sum, room) => sum + room.children, 0);

      message = `🏨 *طلب حجز فندق جديد*\n\n`;
      message += `📍 *الوجهة:* ${destination}\n`;
      message += `🗓️ *تاريخ الوصول:* ${formatDate(startDate)}\n`;
      message += `🗓️ *تاريخ المغادرة:* ${formatDate(endDate)}\n`;
      message += `🚪 *عدد الغرف:* ${rooms.length}\n`;
      message += `👥 *إجمالي البالغين:* ${totalAdults}\n`;
      message += `👶 *إجمالي الأطفال:* ${totalChildren}\n`;
      message += `\n💬 أرجو التواصل معي لإتمام الحجز. شكراً!`;
    }

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://api.whatsapp.com/send?phone=9651811008&text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  // Hotel Room Handlers
  const addRoom = () => {
    setRooms(curr => [...curr, { id: curr.length + 1, adults: 1, children: 0 }]);
  };

  const removeRoom = (index: number) => {
    setRooms(curr => curr.filter((_, i) => i !== index));
  };

  const updateRoom = (index: number, field: 'adults' | 'children', delta: number) => {
    setRooms(curr => curr.map((room, i) => {
      if (i === index) {
        const newVal = Math.max(0, room[field] + delta);
        // Ensure at least 1 adult in a room if decrementing adults
        if (field === 'adults' && newVal < 1) return room;
        return { ...room, [field]: newVal };
      }
      return room;
    }));
  };

  return (
    <div className="w-full max-w-[600px] relative z-40 mx-auto mt-8 flex flex-col items-center gap-3" dir={lang === 'ar' ? 'rtl' : 'ltr'}>

      {/* Tabs - Centered, standalone, big with icons */}
      <div className="flex gap-4 p-1 rounded-2xl w-fit">
        <button
          onClick={() => { setActiveTab('flights'); setStep(0); }}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-[0.6rem] text-base font-bold transition-all ${activeTab === 'flights'
            ? 'bg-white text-gray-900 shadow-xl scale-105'
            : 'bg-white/10 backdrop-blur-md text-white hover:bg-white/20'
            }`}
        >
          <Plane className="w-5 h-5" />
          {t.tabs.flights}
        </button>
        <button
          onClick={() => { setActiveTab('hotel'); setStep(0); }}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-[0.6rem] text-base font-bold transition-all ${activeTab === 'hotel'
            ? 'bg-white text-gray-900 shadow-xl scale-105'
            : 'bg-white/10 backdrop-blur-md text-white hover:bg-white/20'
            }`}
        >
          <BedDouble className="w-5 h-5" />
          {t.tabs.hotel}
        </button>
        <button
          onClick={() => { setActiveTab('trains'); setStep(0); }}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-[0.6rem] text-base font-bold transition-all ${activeTab === 'trains'
            ? 'bg-white text-gray-900 shadow-xl scale-105'
            : 'bg-white/10 backdrop-blur-md text-white hover:bg-white/20'
            }`}
        >
          <TrainFront className="w-5 h-5" />
          {t.tabs.trains}
        </button>
      </div>

      {/* Main Content Container - White Box */}
      <div className="w-full bg-white rounded-[0.6rem] p-4 md:p-5 shadow-2xl border border-white/50 text-start min-h-[240px] flex flex-col relative overflow-hidden transition-all">

        {/* Step Content */}
        <div className="flex-1 flex flex-col justify-center">

          {/* Step 0: Location */}
          {step === 0 && (
            <div className="animate-in fade-in slide-in-from-bottom-8 duration-500 space-y-4">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">
                {activeTab === 'flights' ? tWiz.step1_flight : (activeTab === 'trains' ? tWiz.step1_train : tWiz.step1_hotel)}
              </h2>

              {activeTab === 'flights' || activeTab === 'trains' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <AutocompleteInput
                    label={tWiz.from}
                    value={origin}
                    onChange={setOrigin}
                    icon={MapPin}
                    placeholder={activeTab === 'flights' ? tWiz.currentLocation : tWiz.from}
                    locations={locations}
                  />
                  <AutocompleteInput
                    label={tWiz.to}
                    value={destination}
                    onChange={setDestination}
                    icon={MapPin}
                    placeholder={tWiz.location}
                    locations={locations}
                  />
                </div>
              ) : (
                <div className="w-full">
                  <AutocompleteInput
                    label={tWiz.location}
                    value={destination}
                    onChange={setDestination}
                    icon={MapPin}
                    placeholder={tWiz.step1_hotel}
                    locations={locations}
                  />
                </div>
              )}
            </div>
          )}

          {/* Step 1: Dates */}
          {step === 1 && (
            <div className="animate-in fade-in slide-in-from-bottom-8 duration-500 space-y-4">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">
                {tWiz.step2}
              </h2>

              {/* One-way / Round-trip Toggle */}
              {(activeTab === 'flights' || activeTab === 'trains') && (
                <div className="flex items-center gap-3 mb-2">
                  <button
                    onClick={() => {
                      setTripType('oneway');
                      setEndDate(null);
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all ${tripType === 'oneway'
                      ? 'bg-gray-900 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                  >
                    <ArrowRight className="w-4 h-4" />
                    {lang === 'ar' ? 'ذهاب فقط' : 'One Way'}
                  </button>
                  <button
                    onClick={() => setTripType('roundtrip')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all ${tripType === 'roundtrip'
                      ? 'bg-gray-900 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                  >
                    <ArrowRightLeft className="w-4 h-4" />
                    {lang === 'ar' ? 'ذهاب وعودة' : 'Round Trip'}
                  </button>
                </div>
              )}

              <div className="flex gap-4 w-full">
                <RangeDatePicker
                  label={tripType === 'oneway'
                    ? (lang === 'ar' ? 'تاريخ المغادرة' : 'Departure Date')
                    : tWiz.tripDatesLabel
                  }
                  startDate={startDate}
                  endDate={endDate}
                  onDateChange={(s, e) => { setStartDate(s); setEndDate(e); }}
                  placeholder={tripType === 'oneway'
                    ? (lang === 'ar' ? 'اختر تاريخ المغادرة' : 'Select departure date')
                    : tWiz.tripDatesPlaceholder
                  }
                  lang={lang}
                  tWiz={tWiz}
                  tripType={tripType}
                />
              </div>

              {/* Show selected dates summary for roundtrip */}
              {tripType === 'roundtrip' && startDate && !endDate && (
                <div className="text-sm text-amber-600 font-medium flex items-center gap-2 bg-amber-50 px-3 py-2 rounded-lg">
                  <CalendarIcon className="w-4 h-4" />
                  {lang === 'ar' ? 'الآن اختر تاريخ العودة' : 'Now select your return date'}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Guests */}
          {step === 2 && (
            <div className="animate-in fade-in slide-in-from-bottom-8 duration-500 space-y-4">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">
                {tWiz.step3}
              </h2>

              {activeTab === 'flights' || activeTab === 'trains' ? (
                <div className="flex flex-wrap gap-4 items-center">
                  {/* Adults */}
                  <div className="bg-gray-50 rounded-[0.6rem] p-4 border border-gray-100 min-w-[160px]">
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2 block">{tWiz.adults}</label>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setAdults(Math.max(1, adults - 1))}
                        className="w-10 h-10 rounded-full bg-white shadow-sm border border-gray-200 flex items-center justify-center hover:bg-gray-50 text-lg font-bold"
                      >-</button>
                      <span className="text-2xl font-bold text-gray-900 w-6 text-center">{adults}</span>
                      <button
                        onClick={() => setAdults(adults + 1)}
                        className="w-10 h-10 rounded-full bg-gray-900 text-white shadow-lg shadow-gray-900/20 flex items-center justify-center hover:bg-black text-lg font-bold"
                      >+</button>
                    </div>
                  </div>

                  {/* Children */}
                  <div className="bg-gray-50 rounded-[0.6rem] p-4 border border-gray-100 min-w-[160px]">
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2 block">{tWiz.children}</label>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setChildren(Math.max(0, children - 1))}
                        className="w-10 h-10 rounded-full bg-white shadow-sm border border-gray-200 flex items-center justify-center hover:bg-gray-50 text-lg font-bold"
                      >-</button>
                      <span className="text-2xl font-bold text-gray-900 w-6 text-center">{children}</span>
                      <button
                        onClick={() => setChildren(children + 1)}
                        className="w-10 h-10 rounded-full bg-gray-900 text-white shadow-lg shadow-gray-900/20 flex items-center justify-center hover:bg-black text-lg font-bold"
                      >+</button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 max-h-[250px] overflow-y-auto pr-2 custom-scrollbar">
                  {rooms.map((room, index) => (
                    <div key={room.id} className="bg-gray-50 rounded-[0.6rem] p-4 border border-gray-100 animate-in fade-in slide-in-from-bottom-2 duration-300">
                      <div className="flex justify-between items-center mb-3 border-b border-gray-200 pb-2">
                        <h3 className="text-base font-bold text-gray-900">{tWiz.room} {index + 1}</h3>
                        {rooms.length > 1 && (
                          <button onClick={() => removeRoom(index)} className="text-red-500 text-[10px] font-bold hover:text-red-700 underline flex items-center gap-1">
                            <Trash2 className="w-3 h-3" /> {tWiz.remove}
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Adults Row */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <div className="flex flex-col">
                              <span className="text-sm font-bold text-gray-900">{tWiz.adults}</span>
                              <span className="text-[10px] text-gray-400 font-medium">{tWiz.adultsAge}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateRoom(index, 'adults', -1)}
                              className={`w-7 h-7 rounded-lg flex items-center justify-center text-base font-bold transition-colors ${room.adults <= 1 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                              disabled={room.adults <= 1}
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="text-base font-bold text-gray-900 w-4 text-center">{room.adults}</span>
                            <button
                              onClick={() => updateRoom(index, 'adults', 1)}
                              className="w-7 h-7 rounded-lg bg-emerald-600 text-white flex items-center justify-center text-base font-bold hover:bg-emerald-700 transition-colors shadow-sm"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        </div>

                        {/* Children Row */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <User className="w-3 h-3 text-gray-400 scale-75" />
                            <div className="flex flex-col">
                              <span className="text-sm font-bold text-gray-900">{tWiz.children}</span>
                              <span className="text-[10px] text-gray-400 font-medium">{tWiz.childrenAge}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateRoom(index, 'children', -1)}
                              className={`w-7 h-7 rounded-lg flex items-center justify-center text-base font-bold transition-colors ${room.children <= 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                              disabled={room.children <= 0}
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="text-base font-bold text-gray-900 w-4 text-center">{room.children}</span>
                            <button
                              onClick={() => updateRoom(index, 'children', 1)}
                              className="w-7 h-7 rounded-lg bg-emerald-600 text-white flex items-center justify-center text-base font-bold hover:bg-emerald-700 transition-colors shadow-sm"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={addRoom}
                    className="flex items-center gap-2 text-emerald-600 font-bold hover:text-emerald-700 transition-colors mt-4 text-sm"
                  >
                    <div className="w-5 h-5 rounded-full border-2 border-emerald-600 flex items-center justify-center">
                      <Plus className="w-3 h-3" />
                    </div>
                    {tWiz.addRoom}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation Footer */}
        <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-100">
          {step > 0 ? (
            <button
              onClick={prevStep}
              className="flex items-center gap-2 text-gray-500 font-bold hover:text-gray-900 transition-colors px-3 py-2 text-sm"
            >
              <ArrowRightLeft className="w-4 h-4" /> {tWiz.back}
            </button>
          ) : (
            <div></div> // Spacer
          )}

          {step < 2 ? (() => {
            // Validation: on step 0, require origin and destination for flights, or just destination for hotels
            const isStep0Valid = step !== 0 || (
              (activeTab === 'flights' || activeTab === 'trains')
                ? (origin && destination && /\([A-Z]{3}\)$/.test(origin) && /\([A-Z]{3}\)$/.test(destination))
                : (destination && /\([A-Z]{3}\)$/.test(destination))
            );

            return (
              <button
                onClick={nextStep}
                disabled={!isStep0Valid}
                className={`rounded-[0.6rem] px-8 py-3 font-bold text-base transition-all flex items-center gap-2 ${isStep0Valid
                  ? 'bg-gray-900 text-white hover:bg-black shadow-xl shadow-gray-900/20'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
              >
                {tWiz.next} <ChevronRight className={`w-4 h-4 ${lang === 'ar' ? 'rotate-180' : ''}`} />
              </button>
            );
          })() : (
            <button
              onClick={handleBook}
              className="bg-emerald-600 text-white rounded-[0.6rem] px-8 py-3 font-bold text-base hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-600/30 flex items-center gap-2 animate-in zoom-in duration-300"
            >
              <MessageCircle className="w-5 h-5 fill-white" />
              {tWiz.book}
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

const ActivityCard: React.FC<{ activity: any, lang: 'en' | 'ar' }> = ({ activity, lang }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isGif = activity.video?.toLowerCase().endsWith('.gif');

  const handleMouseEnter = () => {
    if (videoRef.current && !isGif) {
      videoRef.current.play().catch(e => console.log("Video play error", e));
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current && !isGif) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      className="relative flex-none w-[200px] md:w-[220px] aspect-[3/4] rounded-[1rem] overflow-hidden group cursor-pointer snap-center border border-gray-200 transition-all duration-300"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Image */}
      <img
        src={activity.image}
        srcSet={
          activity.imageSmall ? `${activity.imageSmall} 400w, ${activity.imageMedium ? `${activity.imageMedium} 800w, ` : ''}${activity.image} 1200w` : undefined
        }
        alt={activity.title}
        className={`w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 ${activity.video ? 'group-hover:opacity-0' : ''}`}
      />

      {/* Video or GIF */}
      {activity.video && (
        isGif ? (
          <img
            src={activity.video}
            alt={activity.title + " preview"}
            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          />
        ) : (
          <video
            ref={videoRef}
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          >
            {activity.videoWebm && <source src={activity.videoWebm} type="video/webm" />}
            <source src={activity.video} type="video/mp4" />
          </video>
        )
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-90 transition-opacity duration-300 pointer-events-none"></div>

      <div className="absolute top-5 left-5 right-5 flex justify-between items-start z-10 text-white pointer-events-none">
        {activity.logoTitle && (
          <div className="flex flex-col items-start leading-none drop-shadow-md">
            <span className="font-bold text-base">{activity.logoTitle}</span>
            {activity.logoSub && <span className="text-[9px] tracking-widest uppercase opacity-80 mt-0.5">{activity.logoSub}</span>}
          </div>
        )}

        {activity.promo && (
          <div className="flex flex-col items-center drop-shadow-md">
            <span className="text-[10px] opacity-90 mb-0.5 font-medium">{lang === 'ar' ? 'وفروا حتى' : 'Save up to'}</span>
            <div className="flex items-start leading-none">
              <span className="text-3xl font-black text-white tracking-tighter">{activity.promo}</span>
              <span className="text-sm font-bold mt-1">%</span>
            </div>
          </div>
        )}
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-6 flex flex-col items-center text-center z-10 pointer-events-none">
        {activity.hasOverlay && (
          <div className="bg-[#FFB700] text-black text-[10px] font-bold px-2.5 py-1 rounded-full mb-3 shadow-lg uppercase tracking-wide transform scale-90 md:scale-100">
            {lang === 'ar' ? 'لحجزكم كبينة بشرفة' : 'Book a Balcony Cabin'}
          </div>
        )}
        <h3 className="text-lg md:text-xl font-bold text-white mb-1.5 leading-tight drop-shadow-lg">
          {activity.title}
        </h3>
        <p className="text-white/80 text-xs font-medium line-clamp-1">
          {activity.subtitle}
        </p>
      </div>
    </div>
  )
};

const PackageCard: React.FC<{ pkg: any, t: any, lang: 'en' | 'ar' }> = ({ pkg, t, lang }) => {
  const navigate = useNavigate();
  const currentLang = TRANSLATIONS[lang] ? lang : 'en';
  const safeT = TRANSLATIONS[currentLang];

  // WhatsApp booking function
  const handleBookNow = () => {
    // Track the reserve click event
    trackEvent('reserve_click', {
      packageId: pkg.id,
      packageTitle: pkg.title
    });

    const whatsappNumber = '9651811008';
    const message = lang === 'ar'
      ? `مرحباً، أود الاستفسار عن الحجز:\n\n📦 الباقة: ${pkg.title}\n💰 السعر: ${pkg.price} د.ك\n🌙 ${pkg.nights} ليالي\n🗓️ ${pkg.date}\n\nأرجو التواصل معي لإتمام الحجز.`
      : `Hello, I would like to inquire about booking:\n\n📦 Package: ${pkg.title}\n💰 Price: ${pkg.price} KWD\n🌙 ${pkg.nights} nights\n🗓️ ${pkg.date}\n\nPlease contact me to complete the booking.`;
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="group bg-white rounded-[1rem] overflow-hidden border border-gray-200 flex flex-col transition-all duration-300">

      {/* Image Area */}
      <div className="relative h-64 overflow-hidden">
        <LazyImage src={pkg.image} alt={pkg.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />

        {/* Top Left Buttons */}
        <div className="absolute top-4 left-4 flex gap-2 z-9" dir="ltr">
          {/* Force LTR for buttons so they align correctly relative to each other if needed, but flex gap works anyway. 
                   In the image, they are on the left. In RTL, 'left-4' is visually left. */}
          <button className="w-9 h-9 rounded-full bg-white/40 backdrop-blur-md flex items-center justify-center hover:bg-white text-gray-800 transition-colors" aria-label="Add to favorites"><Heart size={16} /></button>
          <button className="w-9 h-9 rounded-full bg-white/40 backdrop-blur-md flex items-center justify-center hover:bg-white text-gray-800 transition-colors" aria-label="Dislike"><ThumbsDown size={16} /></button>
          <button className="w-9 h-9 rounded-full bg-white/40 backdrop-blur-md flex items-center justify-center hover:bg-white text-gray-800 transition-colors" aria-label="Like"><ThumbsUp size={16} /></button>
        </div>
      </div>

      {/* Content Area */}
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex gap-4 mb-4">

          {/* Main Details (Right in RTL) */}
          <div className="flex-1">
            <h3 className="text-xl font-black text-gray-900 mb-3 leading-snug">{pkg.title}</h3>

            <div className="flex items-center gap-2 text-gray-600 text-sm mb-4 font-medium">
              <span>{pkg.date}</span>
              <CalendarIcon className="w-4 h-4 text-gray-500" />
            </div>

            <div className="flex flex-wrap gap-2">
              {pkg.tags?.map((tag: string) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-gray-50 text-xs font-bold text-gray-700 border border-gray-100">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Timeline (Left in RTL) - Hidden on mobile */}
          <div className="hidden md:flex w-1/3 flex-col items-end relative pt-1">
            {/* Vertical Line */}
            <div className="absolute top-2 bottom-6 right-[14.5px] w-[2px] bg-gray-200"></div>

            {pkg.itinerary?.map((city: string, idx: number) => (
              <div key={idx} className="flex items-center justify-end gap-3 mb-5 relative z-10 w-full">
                <span className="text-[10px] md:text-xs font-bold text-gray-500 truncate">{city}</span>
                <div className="w-2.5 h-2.5 rounded-full bg-gray-300 border-2 border-white ring-1 ring-gray-200"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto pt-4 border-t border-gray-100 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBookNow}
              className="bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-3 rounded-xl font-bold text-sm transition-all shadow-md hover:shadow-lg active:scale-95"
            >
              {lang === 'ar' ? 'احجز الآن' : 'Book Now'}
            </button>

            <button
              onClick={() => {
                navigate(lang === 'ar' ? `/package/${pkg.slug}` : `/en/package/${pkg.slug}`, {
                  state: { package: pkg }
                });
              }}
              className="text-xs font-bold text-gray-500 hover:text-emerald-700 transition-colors underline pb-0.5 bg-transparent border-none cursor-pointer"
            >
              {lang === 'ar' ? 'تفاصيل العرض' : 'See Details'}
            </button>
          </div>

          <div className="text-right">
            <span className="text-[10px] text-gray-500 font-medium block mb-0.5">{safeT.packages.priceLabel}</span>
            <div className="flex items-baseline gap-1 flex-row-reverse">
              <span className="text-xs text-gray-500">{lang === 'ar' ? 'د.ك' : 'KWD'}</span>
              <span className="text-3xl font-black text-gray-900">{pkg.price}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Destination Page Component
const DestinationPage = ({
  destination,
  lang,
  onNavigateHome,
  onNavigateToPackages,
  onNavigateToDestinations,
  onNavigateToServices,
  onNavigateToFAQ,
  onNavigateToContact, onNavigateToAbout
}: {
  destination: any;
  lang: 'en' | 'ar';
  onNavigateHome: () => void;
  onNavigateToPackages: () => void;
  onNavigateToDestinations: () => void;
  onNavigateToServices: () => void;
  onNavigateToFAQ: () => void;
  onNavigateToContact: () => void; onNavigateToAbout: () => void;
}) => {
  const currentLang = TRANSLATIONS[lang] ? lang : 'en';
  const t = TRANSLATIONS[currentLang];
  const [packages, setPackages] = useState<Package[]>([]);
  // Initialize with minimal fallback so title always shows
  const [article, setArticle] = useState<Article | any>({
    title: destination.name,
    content: '',
    quote: ''
  });
  const [loading, setLoading] = useState(true);

  const bannerImage = destination.banner || destination.image || '/placeholders/plane.webp';

  console.log('DestinationPage Image Debug:', { id: destination.id, name: destination.name, banner: destination.banner, image: destination.image, final: bannerImage });

  // Fetch packages and article from Django API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        console.log(`Debug DestinationPage: Loading data for id=${destination.id} name=${destination.name}`);

        // Fetch Packages
        const pkgData = await fetchPackages(lang, destination.id);

        // Client-side filtering fallback
        const filteredPackages = pkgData.filter(pkg =>
          pkg.destinations && pkg.destinations.includes(destination.id)
        );
        setPackages(filteredPackages);

        // Fetch Article
        try {
          const artData = await fetchArticles(lang, destination.id);
          if (artData && artData.length > 0) {
            setArticle(artData[0]);
          } else {
            // Fallback to destination object data if available
            const fallbackArticle = destination.articles?.[0] || destination.article;
            if (fallbackArticle) {
              setArticle(fallbackArticle);
            }
          }
        } catch (err) {
          console.warn("Failed to fetch articles:", err);
          // Fallback
          const fallbackArticle = destination.articles?.[0] || destination.article;
          if (fallbackArticle) setArticle(fallbackArticle);
        }

      } catch (error) {
        console.error('Failed to fetch destination data:', error);
        setPackages([]);
      } finally {
        setLoading(false);
      }
    };

    if (destination.id) {
      loadData();
    }
  }, [destination.id, lang]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []); // destination.id dependency removed to prevent scroll jump on same destination

  const pageTitle = lang === 'ar'
    ? `أفضل عروض سياحية إلى ${destination.name} 2026 – 2027`
    : `Best Travel Offers to ${destination.name} 2026 – 2027`;

  return (
    <div className="min-h-screen bg-white" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <SEO
        title={pageTitle}
        description={article.content?.substring(0, 160) || pageTitle}
      />
      <Navbar lang={lang} onNavigateHome={onNavigateHome} onNavigateToPackages={onNavigateToPackages} onNavigateToDestinations={onNavigateToDestinations} onNavigateToServices={onNavigateToServices} onNavigateToFAQ={onNavigateToFAQ} onNavigateToContact={onNavigateToContact} onNavigateToAbout={onNavigateToAbout} />

      {/* Hero Section with Dimmed Banner */}
      <div className="relative h-[60vh] min-h-[400px] overflow-hidden mt-[88px]">
        <img
          src={bannerImage}
          alt={destination.name}
          className="w-full h-full object-cover absolute inset-0"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 md:px-16 lg:px-24 z-10">
          <h1
            className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-4 max-w-4xl mx-auto !leading-[1.3]"
          >
            {lang === 'ar'
              ? `أفضل عروض سياحية إلى ${destination.name} 2026 – 2027`
              : `Best Travel Offers to ${destination.name} 2026 – 2027`
            }
          </h1>
          <p className="text-lg md:text-xl text-white/80 font-medium max-w-2xl mx-auto" style={{ lineHeight: '1.3' }}>
            {lang === 'ar'
              ? `أفضل عروض سياحية إلى ${destination.name} 2026 – 2027`
              : `Best Travel Offers to ${destination.name} 2026 – 2027`
            }
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12">
        {/* Breadcrumb */}
        <div className="mb-8">
          <button
            onClick={onNavigateHome}
            className="text-emerald-600 hover:text-emerald-700 font-bold text-base flex items-center gap-2 transition-colors"
          >
            <ChevronRight className={`w-4 h-4 ${lang === 'ar' ? '' : 'rotate-180'}`} />
            {t.nav.home}
          </button>
        </div>

        {/* Packages Section - Only show if packages exist */}
        {!loading && packages.length > 0 && (
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
              {lang === 'ar' ? `عروض السفر إلى ${destination.name}` : `Travel Offers to ${destination.name}`}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {packages.map((pkg) => (
                  <PackageCard key={pkg.id} pkg={pkg} t={t} lang={lang} />
                ))}
            </div>
          </section>
        )}

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        )}




        {/* Article Section */}
        {article && (
          <section className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
              {article.title || destination.name}
            </h2>

            <article className="prose prose-lg max-w-none">
              {/* Metadata & Rating */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6 not-prose">
                {article.author && (
                  <div className="flex items-center gap-1.5">
                    <User className="w-4 h-4" />
                    <span>{article.author}</span>
                  </div>
                )}
                {article.date && (
                  <div className="flex items-center gap-1.5">
                    <CalendarIcon className="w-4 h-4" />
                    <span>{article.date}</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 bg-amber-50 px-2 py-1 rounded-lg border border-amber-100">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  <span className="font-bold text-gray-900">4.8</span>
                  <span className="text-gray-400">
                    ({Math.floor(Math.random() * (480 - 120 + 1) + 120)} {lang === 'ar' ? 'صوت' : 'reviews'})
                  </span>
                </div>
              </div>

              {article.image && (
                <div className="mb-8 rounded-2xl overflow-hidden shadow-lg">
                  <img src={article.image} alt={article.title} className="w-full h-auto object-cover max-h-[500px]" />
                </div>
              )}
              <div className="text-gray-600 leading-relaxed text-lg mb-8">
                <React.Suspense fallback={null}><ReactMarkdown>{article.content}</ReactMarkdown></React.Suspense>
              </div>

              {/* Quote Block */}
              {article.quote && (
                <blockquote className="relative bg-gradient-to-r from-emerald-50 to-emerald-100/50 rounded-2xl p-8 my-12 border-l-4 border-emerald-500">
                  <Quote className="absolute top-4 right-4 w-12 h-12 text-emerald-200" />
                  <p className="text-xl md:text-2xl font-medium text-gray-800 italic leading-relaxed relative z-10">
                    {article.quote}
                  </p>
                </blockquote>
              )}
            </article>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-100 pt-16 pb-8">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-12 mb-12">
            <div className="col-span-1 md:col-span-1">
              <div className="flex items-center gap-2 mb-6">
                <img src="/assets/images/logo.webp" alt="COMO" className="h-12 w-auto object-contain" />
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                {t.footer.brandDesc}
              </p>
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-400 hover:text-blue-600 hover:border-blue-600 transition-all cursor-pointer"><Facebook className="w-5 h-5" /></div>
                <div className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-400 hover:text-pink-600 hover:border-pink-600 transition-all cursor-pointer"><Instagram className="w-5 h-5" /></div>
                <div className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-400 hover:text-blue-400 hover:border-blue-400 transition-all cursor-pointer"><Twitter className="w-5 h-5" /></div>
              </div>
            </div>

            <div>
              <h4 className="font-bold text-gray-900 mb-6">{t.footer.titles.pages}</h4>
              <ul className="space-y-4 text-gray-500 text-sm">
                {t.footer.links.pages.map((link: { label: string; href: string }) => (
                  <li key={link.label}><a href={link.href} className="hover:text-blue-600 cursor-pointer transition-colors">{link.label}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-6">{t.footer.titles.services}</h4>
              <ul className="space-y-4 text-gray-500 text-sm">
                {t.footer.links.services.map((link: { label: string; href: string }) => (
                  <li key={link.label}><a href={link.href} className="hover:text-blue-600 cursor-pointer transition-colors">{link.label}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-6">{t.footer.titles.legal}</h4>
              <ul className="space-y-4 text-gray-500 text-sm">
                {t.footer.links.legal.map((link: { label: string; href: string }) => (
                  <li key={link.label}><a href={link.href} className="hover:text-blue-600 cursor-pointer transition-colors">{link.label}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-6">{t.footer.titles.support}</h4>
              <ul className="space-y-4 text-gray-500 text-sm">
                {t.footer.links.support.map((link: { label: string; href: string }) => (
                  <li key={link.label}><a href={link.href} className="hover:text-blue-600 cursor-pointer transition-colors">{link.label}</a></li>
                ))}
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-8 text-center text-sm text-gray-400">
            {t.footer.rights}
          </div>
        </div>
      </footer>
    </div>
  );
};

const PackagesPage = ({
  lang,
  onNavigateHome,
  onNavigateToPackages,
  onNavigateToDestinations,
  onNavigateToServices,
  onNavigateToFAQ,
  onNavigateToContact,
  onNavigateToAbout,
  destinations
}: {
  lang: 'en' | 'ar';
  onNavigateHome: () => void;
  onNavigateToPackages: () => void;
  onNavigateToDestinations: () => void;
  onNavigateToServices: () => void;
  onNavigateToFAQ: () => void;
  onNavigateToContact: () => void;
  onNavigateToAbout: () => void;
  destinations: Destination[];
}) => {
  const currentLang = TRANSLATIONS[lang] ? lang : 'en';
  const t = TRANSLATIONS[currentLang];
  const [packages, setPackages] = useState<Package[]>([]);
  const [filteredPackages, setFilteredPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedDestination, setSelectedDestination] = useState<number | null>(null);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(1000);
  const [selectedMaxBudget, setSelectedMaxBudget] = useState(1000);
  const [isDestinationOpen, setIsDestinationOpen] = useState(false);

  // Fetch all packages
  useEffect(() => {
    const loadPackages = async () => {
      try {
        setLoading(true);
        const data = await fetchPackages(lang);

        setPackages(data);
        setFilteredPackages(data);
        // Set min/max budget based on packages
        if (data.length > 0) {
          const prices = data.map(p => Number(p.price));
          const min = Math.min(...prices);
          const max = Math.max(...prices);
          setMinPrice(min);
          setMaxPrice(max);
          setSelectedMaxBudget(max);
        }
      } catch (error) {
        console.error('Failed to fetch packages:', error);
        setPackages([]);
        setFilteredPackages([]);
      } finally {
        setLoading(false);
      }
    };
    loadPackages();
  }, [lang]);

  // Apply filters
  useEffect(() => {
    let result = packages;

    // Filter by destination
    if (selectedDestination !== null) {
      result = result.filter(pkg => pkg.destinations.includes(selectedDestination));
    }

    // Filter by max budget (from minPrice to selectedMaxBudget)
    result = result.filter(pkg => {
      const price = Number(pkg.price);
      return price >= minPrice && price <= selectedMaxBudget;
    });

    setFilteredPackages(result);
  }, [packages, selectedDestination, selectedMaxBudget, minPrice]);

  // Infinite Scroll Logic
  const [visibleCount, setVisibleCount] = useState(24);
  const visiblePackages = filteredPackages.slice(0, visibleCount);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(24);
  }, [selectedDestination, selectedMaxBudget]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 500 &&
        visibleCount < filteredPackages.length
      ) {
        setVisibleCount((prev) => prev + 24);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [visibleCount, filteredPackages.length]);

  return (
    <div className="min-h-screen bg-gray-50" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <SEO
        title="عروض سفر من الكويت 2026 | بكجات سياحية وعروض شهر العسل | شركة كومو"
        description="أقوى عروض السفر من الكويت 2026! بكجات سياحية، شهر عسل، ورحلات عائلية بأسعار تنافسية من كومو للسياحة. احجز وجهتك المفضلة اليوم."
      />
      <Navbar lang={lang} onNavigateHome={onNavigateHome} onNavigateToPackages={onNavigateToPackages} onNavigateToDestinations={onNavigateToDestinations} onNavigateToServices={onNavigateToServices} onNavigateToFAQ={onNavigateToFAQ} onNavigateToContact={onNavigateToContact} onNavigateToAbout={onNavigateToAbout} />

      {/* Header with Hero Image */}
      <div className="relative bg-gray-900 pt-24 pb-12 overflow-hidden h-[400px] flex items-center">
        <img
          src="https://images.unsplash.com/photo-1502003153089-649eb051d8b5?q=80&w=2074&auto=format&fit=crop"
          alt="Travel Offers"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent"></div>
        <div className="container mx-auto px-6 relative z-10">
          <button
            onClick={onNavigateHome}
            className="text-white/80 hover:text-white font-medium text-sm flex items-center gap-2 mb-4 transition-colors"
          >
            <ChevronRight className={`w-4 h-4 ${lang === 'ar' ? '' : 'rotate-180'}`} />
            {t.nav.home}
          </button>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-2 leading-tight">
            {lang === 'ar' ? 'عروض السفر' : 'Travel Offers'}
          </h1>
          <p className="text-xl text-white/90 font-medium">
            {lang === 'ar' ? `${filteredPackages.length} عرض متاح` : `${filteredPackages.length} offers available`}
          </p>
        </div>
      </div>

      {/* Modern Filters */}
      <div className="container mx-auto px-6 -mt-6 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
          <div className="flex flex-wrap gap-6 items-end">
            {/* Destination Filter - Autocomplete Search */}
            <div className="flex-1 min-w-[220px]">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <MapPin className="w-4 h-4 text-emerald-600" />
                {lang === 'ar' ? 'الوجهة' : 'Destination'}
              </label>
              {/* Autocomplete Search Input */}
              <DestinationAutocomplete
                lang={lang}
                destinations={destinations}
                selectedDestination={selectedDestination}
                onSelect={(destId) => setSelectedDestination(destId)}
              />
            </div>

            {/* Budget - Single Slider */}
            <div className="flex-1 min-w-[300px]">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <Wallet className="w-4 h-4 text-emerald-600" />
                {lang === 'ar' ? 'الحد الأقصى للميزانية' : 'Maximum Budget'}
              </label>
              <div className="bg-white rounded-xl border-2 border-gray-100 p-5 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <div className="text-center">
                    <span className="text-xs text-gray-400 block mb-1">{lang === 'ar' ? 'من' : 'From'}</span>
                    <span className="text-lg font-bold text-gray-500">{minPrice}</span>
                  </div>
                  <div className="flex-1 mx-4 h-0.5 bg-gradient-to-r from-gray-200 via-emerald-400 to-gray-200 rounded" />
                  <div className="text-center">
                    <span className="text-xs text-gray-400 block mb-1">{lang === 'ar' ? 'إلى' : 'Up to'}</span>
                    <span className="text-2xl font-black text-emerald-600">{selectedMaxBudget}</span>
                    <span className="text-sm text-gray-400 font-medium mx-1">{lang === 'ar' ? 'د.ك' : 'KWD'}</span>
                  </div>
                </div>
                {/* RTL slider: max on left, min on right */}
                <div>
                  <input
                    type="range"
                    min={minPrice}
                    max={maxPrice}
                    value={selectedMaxBudget}
                    onChange={(e) => setSelectedMaxBudget(Number(e.target.value))}
                    className="w-full h-3 bg-gray-200 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-emerald-500 [&::-webkit-slider-thumb]:border-4 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-emerald-500 [&::-moz-range-thumb]:border-4 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow-lg [&::-moz-range-thumb]:cursor-pointer"
                    style={{
                      direction: 'rtl',
                      background: `linear-gradient(to right, #e5e7eb 0%, #e5e7eb ${((maxPrice - selectedMaxBudget) / (maxPrice - minPrice || 1)) * 100}%, #10b981 ${((maxPrice - selectedMaxBudget) / (maxPrice - minPrice || 1)) * 100}%, #10b981 100%)`
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Clear Filters Button */}
            <button
              onClick={() => {
                setSelectedDestination(null);
                setSelectedMaxBudget(maxPrice);
              }}
              className="px-6 py-3.5 text-sm font-bold text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all border-2 border-gray-100 hover:border-red-200 shadow-sm"
            >
              {lang === 'ar' ? 'مسح الفلاتر' : 'Clear All'}
            </button>
          </div>
        </div>
      </div>

      {/* Packages Grid */}
      <main className="container mx-auto px-6 py-8">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : filteredPackages.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            {lang === 'ar' ? 'لا توجد عروض مطابقة' : 'No matching offers found'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visiblePackages.map((pkg) => (
              <PackageCard key={pkg.id} pkg={pkg} t={t} lang={lang} />
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t py-8">
        <div className="container mx-auto px-6 text-center text-sm text-gray-400">
          {t.footer.rights}
        </div>
      </footer>
    </div>
  );
};

// Destinations Page Component
const DestinationsPage = ({
  lang,
  onNavigateHome,
  onNavigateToPackages,
  onNavigateToDestination,
  onNavigateToDestinations,
  onNavigateToServices,
  onNavigateToFAQ,
  onNavigateToContact, onNavigateToAbout
}: {
  lang: 'en' | 'ar';
  onNavigateHome: () => void;
  onNavigateToPackages: () => void;
  onNavigateToDestinations: () => void;
  onNavigateToDestination: (dest: Destination) => void;
  onNavigateToServices: () => void;
  onNavigateToFAQ: () => void;
  onNavigateToContact: () => void; onNavigateToAbout: () => void;
}) => {
  const currentLang = TRANSLATIONS[lang] ? lang : 'en';
  const t = TRANSLATIONS[currentLang];
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch all destinations
  useEffect(() => {
    const loadDestinations = async () => {
      try {
        setLoading(true);
        const data = await fetchDestinations(lang);
        setDestinations(data);
      } catch (error) {
        console.error('Failed to fetch destinations:', error);
        setDestinations([]);
      } finally {
        setLoading(false);
      }
    };
    loadDestinations();
  }, [lang]);

  return (
    <div className="min-h-screen bg-gray-50" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <SEO
        title="أفضل الوجهات السياحية 2026 | بكجات سفر مميزة | شركة كومو"
        description="اكتشف أجمل الوجهات العالمية مع شركة كومو للسياحة. بكجات متنوعة للعوائل والعرسان تشمل أفضل الأنشطة والفنادق بأسعار حصرية من الكويت. احجز رحلة العمر الآن!"
      />
      <Navbar lang={lang} onNavigateHome={onNavigateHome} onNavigateToPackages={onNavigateToPackages} onNavigateToDestinations={onNavigateToDestinations} onNavigateToServices={onNavigateToServices} onNavigateToFAQ={onNavigateToFAQ} onNavigateToContact={onNavigateToContact} onNavigateToAbout={onNavigateToAbout} />

      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-700 pt-32 pb-16">
        <div className="container mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {lang === 'ar' ? 'الوجهات' : 'Destinations'}
          </h1>
          <p className="text-white/80 text-lg">
            {lang === 'ar' ? 'اكتشف جميع الوجهات السياحية المتاحة' : 'Explore all available travel destinations'}
          </p>
        </div>
      </div>

      {/* Destinations Grid */}
      <main className="container mx-auto px-6 py-12">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="rounded-2xl overflow-hidden shadow-lg bg-gray-100">
                <div className="aspect-[4/5] bg-gray-200 animate-pulse relative">
                  <div className="absolute inset-x-0 bottom-0 p-4 space-y-2">
                    <div className="h-6 bg-gray-300 rounded w-2/3 animate-pulse" />
                    <div className="h-4 bg-gray-300 rounded w-1/2 animate-pulse" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : destinations.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            {lang === 'ar' ? 'لا توجد وجهات متاحة' : 'No destinations available'}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {[...destinations].sort((a, b) => (b.package_count || 0) - (a.package_count || 0)).map((dest) => (
              <div
                key={dest.id}
                className="group cursor-pointer"
                onClick={() => onNavigateToDestination(dest)}
              >
                <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-lg">
                  <LazyImage
                    src={dest.image || '/placeholders/plane.webp'}
                    alt={dest.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-bold text-lg mb-1">{dest.name}</h3>
                    {dest.package_count !== undefined && (
                      <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full">
                        <Briefcase className="w-3.5 h-3.5 text-white" />
                        <span className="text-white text-xs font-medium">
                          {dest.package_count > 0
                            ? `${dest.package_count} ${lang === 'ar' ? 'عروض متاحة' : 'Available Packages'}`
                            : (lang === 'ar' ? 'لا توجد عروض' : 'No packages')
                          }
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t py-8">
        <div className="container mx-auto px-6 text-center text-sm text-gray-400">
          {t.footer.rights}
        </div>
      </footer>
    </div>
  );
};

const App = () => {
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    // Detect Safari
    const isSafariUA = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    setIsSafari(isSafariUA);
  }, []);
  // Calculate initial route from URL path
  const getInitialRoute = (pathInput?: string): { type: 'home' | 'services' | 'flightBooking' | 'hotelBooking' | 'carRental' | 'delegationReception' | 'visaServices' | 'trainBooking' | 'footballBooking' | 'tripOrganizing' | 'faq' | 'contact' | 'about' | 'packages' | 'destinations' | 'terms' | 'privacy' } => {
    const path = decodeURIComponent(pathInput || window.location.pathname);
    if (path.includes('/services') || path.includes('/خدماتنا')) {
      return { type: 'services' };
    } else if (path.includes('/حجز-تذاكر-الطيران') || path.includes('/flight-booking')) {
      return { type: 'flightBooking' };
    } else if (path.includes('/حجوزات-الفنادق') || path.includes('/hotel-booking')) {
      return { type: 'hotelBooking' };
    } else if (path.includes('/تأجير-السيارات') || path.includes('/car-rental')) {
      return { type: 'carRental' };
    } else if (path.includes('/استقبال-الوفود') || path.includes('/delegation-reception')) {
      return { type: 'delegationReception' };
    } else if (path.includes('/التأشيرات') || path.includes('/visa-services')) {
      return { type: 'visaServices' };
    } else if (path.includes('/حجز-القطارات') || path.includes('/حجز-تذاكر-القطارات') || path.includes('/train-booking')) {
      return { type: 'trainBooking' };
    } else if (path.includes('/حجز-المباريات') || path.includes('/حجز-تذاكر-المباريات') || path.includes('/football-booking')) {
      return { type: 'footballBooking' };
    } else if (path.includes('/تنظيم-الرحلات') || path.includes('/trip-organizing')) {
      return { type: 'tripOrganizing' };
    } else if (path.includes('/faq') || path.includes('/الأسئلة-الشائعة')) {
      return { type: 'faq' };
    } else if (path.includes('/contact') || path.includes('/اتصل-بنا')) {
      return { type: 'contact' };
    } else if (path.includes('/about') || path.includes('/من-نحن')) {
      return { type: 'about' };
    } else if (path.includes('/terms') || path.includes('/الشروط-والأحكام')) {
      return { type: 'terms' };
    } else if (path.includes('/privacy') || path.includes('/سياسة-الخصوصية')) {
      return { type: 'privacy' };
    } else if (path.includes('/destinations') || path.includes('/الوجهات')) {
      return { type: 'destinations' };
    } else if (path.includes('/packages') || path.includes('/عروض-السفر')) {
      return { type: 'packages' };
    }
    return { type: 'home' };
  };

  const getInitialLang = (): 'en' | 'ar' => {
    const path = decodeURIComponent(window.location.pathname);
    if (path.includes('/en') || path.includes('/en/')) {
      return 'en';
    }
    return 'ar';
  };

  const [lang, setLang] = useState<'en' | 'ar'>(getInitialLang);
  type PageState =
    | { type: 'home' }
    | { type: 'services' }
    | { type: 'flightBooking' }
    | { type: 'hotelBooking' }
    | { type: 'carRental' }
    | { type: 'delegationReception' }
    | { type: 'visaServices' }
    | { type: 'trainBooking' }
    | { type: 'footballBooking' }
    | { type: 'tripOrganizing' }
    | { type: 'faq' }
    | { type: 'contact' }
    | { type: 'about' }
    | { type: 'terms' }
    | { type: 'privacy' }
    | { type: 'destination'; destination: Destination }
    | { type: 'package'; pkg: Package }
    | { type: 'packages' }
    | { type: 'destinations' };
  const [currentRoute, setCurrentRoute] = useState<PageState>(getInitialRoute);

  const location = useLocation();
  useEffect(() => {
    // Use window.location.pathname directly for stability
    setCurrentRoute(getInitialRoute());
  }, [location.pathname]);

  const currentLang = TRANSLATIONS[lang] ? lang : 'en';
  const t = TRANSLATIONS[currentLang];
  const scrollRef = useRef<HTMLDivElement>(null);
  const activitiesScrollRef = useRef<HTMLDivElement>(null);
  const packagesScrollRef = useRef<HTMLDivElement>(null);
  const destinationsScrollRef = useRef<HTMLDivElement>(null);

  // Navigation visibility state for Activities
  const [showActivitiesNav, setShowActivitiesNav] = useState(true);
  const lastScrollY = useRef(0);

  const navigateToFAQ = () => {
    const newPath = lang === 'en' ? '/en/faq' : '/الأسئلة-الشائعة';
    window.history.pushState({}, '', newPath);
    setCurrentRoute({ type: 'faq' });
    window.scrollTo(0, 0);
  };

  const navigateToContact = () => {
    const newPath = lang === 'en' ? '/en/contact' : '/اتصل-بنا';
    window.history.pushState({}, '', newPath);
    setCurrentRoute({ type: 'contact' });
    window.scrollTo(0, 0);
  };

  const navigateToAbout = () => {
    const newPath = lang === 'en' ? '/en/about' : '/من-نحن';
    window.history.pushState({}, '', newPath);
    setCurrentRoute({ type: 'about' });
    window.scrollTo(0, 0);
  };

  const navigateToTerms = () => {
    const newPath = lang === 'en' ? '/en/terms' : '/الشروط-والأحكام';
    window.history.pushState({}, '', newPath);
    setCurrentRoute({ type: 'terms' });
    window.scrollTo(0, 0);
  };

  const navigateToPrivacy = () => {
    const newPath = lang === 'en' ? '/en/privacy' : '/سياسة-الخصوصية';
    window.history.pushState({}, '', newPath);
    setCurrentRoute({ type: 'privacy' });
    window.scrollTo(0, 0);
  };

  const navigateToFlightBooking = () => {
    const newPath = lang === 'en' ? '/en/flight-booking' : '/حجز-تذاكر-الطيران';
    window.history.pushState({}, '', newPath);
    setCurrentRoute({ type: 'flightBooking' });
    window.scrollTo(0, 0);
  };

  const navigateToHotelBooking = () => {
    const newPath = lang === 'en' ? '/en/hotel-booking' : '/حجوزات-الفنادق';
    window.history.pushState({}, '', newPath);
    setCurrentRoute({ type: 'hotelBooking' });
    window.scrollTo(0, 0);
  };

  const navigateToCarRental = () => {
    const newPath = lang === 'en' ? '/en/car-rental' : '/تأجير-السيارات';
    window.history.pushState({}, '', newPath);
    setCurrentRoute({ type: 'carRental' });
    window.scrollTo(0, 0);
  };

  const navigateToDelegationReception = () => {
    const newPath = lang === 'en' ? '/en/delegation-reception' : '/استقبال-الوفود';
    window.history.pushState({}, '', newPath);
    setCurrentRoute({ type: 'delegationReception' });
    window.scrollTo(0, 0);
  };

  const navigateToVisaServices = () => {
    const newPath = lang === 'en' ? '/en/visa-services' : '/التأشيرات';
    window.history.pushState({}, '', newPath);
    setCurrentRoute({ type: 'visaServices' });
    window.scrollTo(0, 0);
  };

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Navigation visibility state for Packages
  const [canScrollPackagesLeft, setCanScrollPackagesLeft] = useState(false);
  const [canScrollPackagesRight, setCanScrollPackagesRight] = useState(true);

  // Navigation visibility state for Destinations
  const [canScrollDestinationsLeft, setCanScrollDestinationsLeft] = useState(false);
  const [canScrollDestinationsRight, setCanScrollDestinationsRight] = useState(true);
  const [hasScrolledDestinations, setHasScrolledDestinations] = useState(false);

  // Destinations from API
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [destinationsLoading, setDestinationsLoading] = useState(true);

  // Mobile check
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Packages from API for home page
  const [homePackages, setHomePackages] = useState<Package[]>([]);
  const [homePackagesLoading, setHomePackagesLoading] = useState(true);

  // Fetch destinations from API
  useEffect(() => {
    const loadDestinations = async () => {
      try {
        setDestinationsLoading(true);
        const data = await fetchDestinations(lang);
        // Filter destinations to only show those with packages and sort by package count
        const filteredData = data
          .filter(d => (d.package_count || 0) > 0)
          .sort((a, b) => (b.package_count || 0) - (a.package_count || 0));
        setDestinations(filteredData);
      } catch (error) {
        console.error('Failed to fetch destinations:', error);
        setDestinations([]);
      } finally {
        setDestinationsLoading(false);
      }
    };
    loadDestinations();
  }, [lang]);

  // Initialize destinations scroll state after data loads
  useEffect(() => {
    if (!destinationsLoading && destinations.length > 0) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        if (destinationsScrollRef.current) {
          const { scrollWidth, clientWidth } = destinationsScrollRef.current;
          // If content is wider than container, can scroll
          const canScroll = scrollWidth > clientWidth;
          // In Arabic (RTL), scroll starts from right, so LEFT button is enabled to scroll left
          // In English (LTR), scroll starts from left, so RIGHT button is enabled to scroll right
          if (lang === 'ar') {
            setCanScrollDestinationsLeft(canScroll);
            setCanScrollDestinationsRight(false);
          } else {
            setCanScrollDestinationsLeft(false);
            setCanScrollDestinationsRight(canScroll);
          }
        }
      }, 100);
    }
  }, [destinationsLoading, destinations, lang]);
  // Famous destinations to prioritize on home page
  const FAMOUS_DESTINATIONS = [
    // Arabic names
    'لندن', 'ابو ظبي', 'أبو ظبي', 'مدريد', 'برشلونة', 'كورشوفيل', 'دبي',
    // English names
    'London', 'Abu Dhabi', 'Madrid', 'Barcelona', 'Courchevel', 'Dubai'
  ];

  // Fetch all packages for home page
  useEffect(() => {
    const loadPackages = async () => {
      try {
        setHomePackagesLoading(true);
        const data = await fetchPackages(lang);

        // Map for optimizing images from API
        const OPTIMIZED_IMAGE_MAP: Record<string, string> = {
          // Legoland & Mecca
          "https://comotravels.com/media/packages/legoland-water-park.jpg": "/assets/images/packages/legoland_medium.webp?v=2",
          "https://comotravels.com/media/packages/%D9%85%D9%83%D8%A9-%D9%A0--_wjnbOFv.jpg": "/assets/images/packages/mecca_medium.webp?v=2",
          // External Images if they come from API (just in case)
          "https://estatly.ae/wp-content/uploads/2024/04/img_0994.jpg.webp": "/assets/images/packages/uae_medium.webp",
          "https://images.unsplash.com/photo-1573843981267-be1999ff37cd?auto=format&fit=crop&q=80&w=800": "/assets/images/packages/maldives_bliss_medium.webp",
          "https://dynamic-media-cdn.tripadvisor.com/media/photo-o/2b/0c/de/66/caption.jpg": "/assets/images/packages/maldives_luxury_medium.webp",
          "https://images.unsplash.com/photo-1590523277543-a94d2e4eb00b?auto=format&fit=crop&q=80&w=800": "/assets/images/packages/maldives_ocean_medium.webp",
          // Lighthouse Optimization (New Additions)
          "https://pisa1940.com/cdn/shop/articles/duomo_milano_pisa.png?v=1709552807": "/assets/images/destinations/milan_medium.webp",
          "https://images.unsplash.com/photo-1595867818082-083862f3d630?w=800&q=80": "/assets/images/destinations/munich_medium.webp",
          "https://images.unsplash.com/photo-1573108037329-37aa135a142e?w=800&q=80": "/assets/images/destinations/geneva_medium.webp",
          "https://images.unsplash.com/photo-1519677100203-a0e668c92439?w=800&q=80": "/assets/images/destinations/prague_medium.webp",
          "https://images.unsplash.com/photo-1539037116277-4db20889f2d4?w=800&q=80": "/assets/images/destinations/madrid_medium.webp?v=2",
          "https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=800&q=80": "/assets/images/destinations/rome_medium.webp",
          "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=800&q=80": "/assets/images/destinations/london_medium.webp?v=2",
          "https://images.unsplash.com/photo-1528181304800-259b08848526?w=800&q=80": "/assets/images/destinations/thailand_pkg_medium.webp",
          "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=80": "/assets/images/packages/uae_medium.webp"
        };

        // Use backend priority ordering directly, just optimize images
        const packagesWithOptimizedImages = data.map(pkg => ({
          ...pkg,
          image: OPTIMIZED_IMAGE_MAP[pkg.image] || pkg.image
        }));

        setHomePackages(packagesWithOptimizedImages);
      } catch (error) {
        console.error('Failed to fetch packages:', error);
        setHomePackages([]);
      } finally {
        setHomePackagesLoading(false);
      }
    };
    loadPackages();
  }, [lang]);

  useEffect(() => {
    const parseRoute = () => {
      const pathname = decodeURIComponent(window.location.pathname);

      // Check for English destination routes: /en/destination/{slug}
      const enDestMatch = pathname.match(/^\/en\/destination\/([a-zA-Z-]+)$/);
      if (enDestMatch) {
        setLang('en');
        setCurrentRoute({ type: 'destination', slug: enDestMatch[1] });
        window.scrollTo(0, 0);
        return;
      }

      // Check for packages routes FIRST (before Arabic destination)
      if (pathname === '/packages' || pathname === '/عروض-السفر') {
        setLang('ar');
        setCurrentRoute({ type: 'packages' });
        window.scrollTo(0, 0);
        return;
      }
      if (pathname === '/en/packages') {
        setLang('en');
        setCurrentRoute({ type: 'packages' });
        window.scrollTo(0, 0);
        return;
      }

      // Check for services routes
      if (pathname === '/services' || pathname === '/خدماتنا') {
        setLang('ar');
        setCurrentRoute({ type: 'services' });
        window.scrollTo(0, 0);
        return;
      }
      if (pathname === '/en/services') {
        setLang('en');
        setCurrentRoute({ type: 'services' });
        window.scrollTo(0, 0);
        return;
      }

      // Check for Train Booking
      if (pathname === '/حجز-القطارات' || pathname === '/حجز-تذاكر-القطارات') {
        setLang('ar');
        setCurrentRoute({ type: 'trainBooking' });
        window.scrollTo(0, 0);
        return;
      }
      if (pathname === '/en/train-booking') {
        setLang('en');
        setCurrentRoute({ type: 'trainBooking' });
        window.scrollTo(0, 0);
        return;
      }

      // Check for Football Booking
      if (pathname === '/حجز-المباريات' || pathname === '/حجز-تذاكر-المباريات') {
        setLang('ar');
        setCurrentRoute({ type: 'footballBooking' });
        window.scrollTo(0, 0);
        return;
      }
      if (pathname === '/en/football-booking') {
        setLang('en');
        setCurrentRoute({ type: 'footballBooking' });
        window.scrollTo(0, 0);
        return;
      }

      // Check for Trip Organizing
      if (pathname === '/تنظيم-الرحلات') {
        setLang('ar');
        setCurrentRoute({ type: 'tripOrganizing' });
        window.scrollTo(0, 0);
        return;
      }
      if (pathname === '/en/trip-organizing') {
        setLang('en');
        setCurrentRoute({ type: 'tripOrganizing' });
        window.scrollTo(0, 0);
        return;
      }

      // Check for destinations page routes
      if (pathname === '/الوجهات') {
        setLang('ar');
        setCurrentRoute({ type: 'destinations' });
        window.scrollTo(0, 0);
        return;
      }
      if (pathname === '/en/destinations') {
        setLang('en');
        setCurrentRoute({ type: 'destinations' });
        window.scrollTo(0, 0);
        return;
      }

      // Check for flight booking routes
      if (pathname === '/حجز-تذاكر-الطيران') {
        setLang('ar');
        setCurrentRoute({ type: 'flightBooking' });
        window.scrollTo(0, 0);
        return;
      }
      if (pathname === '/en/flight-booking' || pathname === '/flight-booking') {
        setLang(pathname.startsWith('/en') ? 'en' : 'ar');
        setCurrentRoute({ type: 'flightBooking' });
        window.scrollTo(0, 0);
        return;
      }

      // Check for hotel booking routes
      if (pathname === '/حجوزات-الفنادق') {
        setLang('ar');
        setCurrentRoute({ type: 'hotelBooking' });
        window.scrollTo(0, 0);
        return;
      }
      if (pathname === '/en/hotel-booking' || pathname === '/hotel-booking') {
        setLang(pathname.startsWith('/en') ? 'en' : 'ar');
        setCurrentRoute({ type: 'hotelBooking' });
        window.scrollTo(0, 0);
        return;
      }

      // Check for car rental routes
      if (pathname === '/تأجير-السيارات') {
        setLang('ar');
        setCurrentRoute({ type: 'carRental' });
        window.scrollTo(0, 0);
        return;
      }
      if (pathname === '/en/car-rental' || pathname === '/car-rental') {
        setLang(pathname.startsWith('/en') ? 'en' : 'ar');
        setCurrentRoute({ type: 'carRental' });
        window.scrollTo(0, 0);
        return;
      }

      // Check for delegation reception routes
      if (pathname === '/استقبال-الوفود') {
        setLang('ar');
        setCurrentRoute({ type: 'delegationReception' });
        window.scrollTo(0, 0);
        return;
      }
      if (pathname === '/en/delegation-reception' || pathname === '/delegation-reception') {
        setLang(pathname.startsWith('/en') ? 'en' : 'ar');
        setCurrentRoute({ type: 'delegationReception' });
        window.scrollTo(0, 0);
        return;
      }

      // Check for visa services routes
      if (pathname === '/التأشيرات') {
        setLang('ar');
        setCurrentRoute({ type: 'visaServices' });
        window.scrollTo(0, 0);
        return;
      }
      if (pathname === '/en/visa-services' || pathname === '/visa-services') {
        setLang(pathname.startsWith('/en') ? 'en' : 'ar');
        setCurrentRoute({ type: 'visaServices' });
        window.scrollTo(0, 0);
        return;
      }

      // Check for FAQ routes
      if (pathname === '/الأسئلة-الشائعة') {
        setLang('ar');
        setCurrentRoute({ type: 'faq' });
        window.scrollTo(0, 0);
        return;
      }
      if (pathname === '/en/faq' || pathname === '/faq') {
        setLang(pathname.startsWith('/en') ? 'en' : 'ar');
        setCurrentRoute({ type: 'faq' });
        window.scrollTo(0, 0);
        return;
      }

      // Check for contact routes
      if (pathname === '/اتصل-بنا') {
        setLang('ar');
        setCurrentRoute({ type: 'contact' });
        window.scrollTo(0, 0);
        return;
      }
      if (pathname === '/en/contact' || pathname === '/contact') {
        setLang(pathname.startsWith('/en') ? 'en' : 'ar');
        setCurrentRoute({ type: 'contact' });
        window.scrollTo(0, 0);
        return;
      }

      // Check for about routes
      if (pathname === '/من-نحن') {
        setLang('ar');
        setCurrentRoute({ type: 'about' });
        window.scrollTo(0, 0);
        return;
      }
      if (pathname === '/en/about' || pathname === '/about') {
        setLang(pathname.startsWith('/en') ? 'en' : 'ar');
        setCurrentRoute({ type: 'about' });
        window.scrollTo(0, 0);
        return;
      }

      // Check for terms routes
      if (pathname === '/الشروط-والأحكام') {
        setLang('ar');
        setCurrentRoute({ type: 'terms' });
        window.scrollTo(0, 0);
        return;
      }
      if (pathname === '/en/terms' || pathname === '/terms') {
        setLang(pathname.startsWith('/en') ? 'en' : 'ar');
        setCurrentRoute({ type: 'terms' });
        window.scrollTo(0, 0);
        return;
      }

      // Check for privacy routes
      if (pathname === '/سياسة-الخصوصية') {
        setLang('ar');
        setCurrentRoute({ type: 'privacy' });
        window.scrollTo(0, 0);
        return;
      }
      if (pathname === '/en/privacy' || pathname === '/privacy') {
        setLang(pathname.startsWith('/en') ? 'en' : 'ar');
        setCurrentRoute({ type: 'privacy' });
        window.scrollTo(0, 0);
        return;
      }

      // Check for Arabic destination routes: /{arabic - slug}
      // Arabic slugs start with أفضل-العروض-السياحية
      if (pathname.startsWith('/افضل-العروض-السياحية') || pathname.match(/^\/[\u0600-\u06FF]/)) {
        const slugAr = pathname.slice(1); // Remove leading /
        setLang('ar');
        setCurrentRoute({ type: 'destination', slug: slugAr, isArabicSlug: true });
        window.scrollTo(0, 0);
        return;
      }

      // Check for old format: /destination/{slug}
      const destMatch = pathname.match(/^\/destination\/([a-zA-Z-]+)$/);
      if (destMatch) {
        setLang('ar');
        setCurrentRoute({ type: 'destination', slug: destMatch[1] });
        window.scrollTo(0, 0);
        return;
      }

      // Default home routes
      if (pathname === '/en' || pathname.startsWith('/en/')) {
        setLang('en');
      } else {
        setLang('ar');
      }
      setCurrentRoute({ type: 'home' });
    };

    parseRoute();
    window.addEventListener('popstate', parseRoute);
    return () => window.removeEventListener('popstate', parseRoute);
  }, []);

  const navigateToDestination = (dest: any) => {
    let newPath: string;
    if (lang === 'en') {
      newPath = `/en/destination/${dest.slug}`;
      window.history.pushState({}, '', newPath);
      setCurrentRoute({ type: 'destination', slug: dest.slug });
    } else {
      // Use Arabic slug for Arabic mode (slug_ar from API)
      const slugToUse = dest.slug_ar || dest.slugAr || dest.slug;
      newPath = `/${slugToUse}`;
      window.history.pushState({}, '', newPath);
      setCurrentRoute({ type: 'destination', slug: slugToUse, isArabicSlug: !!(dest.slug_ar || dest.slugAr) });
    }
    window.scrollTo(0, 0);
  };

  const navigateToPackages = () => {
    const newPath = lang === 'en' ? '/en/packages' : '/عروض-السفر';
    window.history.pushState({}, '', newPath);
    setCurrentRoute({ type: 'packages' });
    window.scrollTo(0, 0);
  };

  const navigateHome = () => {
    const newPath = lang === 'en' ? '/en' : '/';
    window.history.pushState({}, '', newPath);
    setCurrentRoute({ type: 'home' });
    window.scrollTo(0, 0);
  };

  const navigateToAllDestinations = () => {
    const newPath = lang === 'en' ? '/en/destinations' : '/الوجهات';
    window.history.pushState({}, '', newPath);
    setCurrentRoute({ type: 'destinations' });
    window.scrollTo(0, 0);
  };

  const navigateToServices = () => {
    const newPath = lang === 'en' ? '/en/services' : '/خدماتنا';
    window.history.pushState({}, '', newPath);
    setCurrentRoute({ type: 'services' });
    window.scrollTo(0, 0);
  };

  const checkScroll = () => {
    if (activitiesScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = activitiesScrollRef.current;

      const absScroll = Math.abs(scrollLeft);
      const maxScroll = scrollWidth - clientWidth;
      const atStart = absScroll < 10;
      const atEnd = Math.abs(absScroll - maxScroll) < 10;

      if (lang === 'ar') {
        setCanScrollLeft(!atEnd);
        setCanScrollRight(!atStart);
      } else {
        setCanScrollLeft(!atStart);
        setCanScrollRight(!atEnd);
      }
    }
  };

  const checkPackagesScroll = () => {
    if (packagesScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = packagesScrollRef.current;
      const absScroll = Math.abs(scrollLeft);
      const maxScroll = scrollWidth - clientWidth;
      const atStart = absScroll < 10;
      const atEnd = Math.abs(absScroll - maxScroll) < 10;

      if (lang === 'ar') {
        setCanScrollPackagesLeft(!atEnd);
        setCanScrollPackagesRight(!atStart);
      } else {
        setCanScrollPackagesLeft(!atStart);
        setCanScrollPackagesRight(!atEnd);
      }
    }
  };

  const checkDestinationsScroll = () => {
    if (destinationsScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = destinationsScrollRef.current;
      const absScroll = Math.abs(scrollLeft);
      const maxScroll = scrollWidth - clientWidth;
      const atStart = absScroll < 10;
      const atEnd = Math.abs(absScroll - maxScroll) < 10;

      // Mark that user has started scrolling (for showing small nav buttons)
      if (!atStart) {
        setHasScrolledDestinations(true);
      }

      if (lang === 'ar') {
        setCanScrollDestinationsLeft(!atEnd);
        setCanScrollDestinationsRight(!atStart);
      } else {
        setCanScrollDestinationsLeft(!atStart);
        setCanScrollDestinationsRight(!atEnd);
      }
    }
  };

  useEffect(() => {
    checkScroll();
    checkPackagesScroll();
    checkDestinationsScroll();
    window.addEventListener('resize', checkScroll);
    window.addEventListener('resize', checkPackagesScroll);
    window.addEventListener('resize', checkDestinationsScroll);
    return () => {
      window.removeEventListener('resize', checkScroll);
      window.removeEventListener('resize', checkPackagesScroll);
      window.removeEventListener('resize', checkDestinationsScroll);
    };
  }, [lang]);

  const scrollActivities = (direction: 'left' | 'right') => {
    if (activitiesScrollRef.current) {
      const { current } = activitiesScrollRef;
      const scrollAmount = 300;

      if (direction === 'left') {
        current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      } else {
        current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }

      setTimeout(checkScroll, 300);
      setTimeout(checkScroll, 500);
    }
  };

  const scrollPackages = (direction: 'left' | 'right') => {
    if (packagesScrollRef.current) {
      const { current } = packagesScrollRef;
      const scrollAmount = 350;

      if (direction === 'left') {
        current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      } else {
        current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }

      setTimeout(checkPackagesScroll, 300);
      setTimeout(checkPackagesScroll, 500);
    }
  };

  const scrollDestinations = (direction: 'left' | 'right') => {
    if (destinationsScrollRef.current) {
      const { current } = destinationsScrollRef;
      const scrollAmount = 300;

      if (direction === 'left') {
        current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      } else {
        current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }

      setTimeout(checkDestinationsScroll, 300);
      setTimeout(checkDestinationsScroll, 500);
    }
  };

  // Find destination for destination pages - use API destinations
  const currentDestination = currentRoute.type === 'destination'
    ? destinations.find(d => d.slug === currentRoute.slug || d.slug_ar === currentRoute.slug)
    : null;

  // If on destination page and destination exists, render DestinationPage
  if (currentRoute.type === 'destination' && currentDestination) {
    return (
      <DestinationPage
        destination={currentDestination}
        lang={lang}
        onNavigateHome={navigateHome}
        onNavigateToPackages={navigateToPackages}
        onNavigateToDestinations={navigateToAllDestinations}
        onNavigateToServices={navigateToServices}
        onNavigateToFAQ={navigateToFAQ}
        onNavigateToContact={navigateToContact} onNavigateToAbout={navigateToAbout}
      />
    );
  }

  // If on packages page, render PackagesPage
  if (currentRoute.type === 'packages') {
    return (
      <PackagesPage
        lang={lang}
        onNavigateHome={navigateHome}
        onNavigateToPackages={navigateToPackages}
        onNavigateToDestinations={navigateToAllDestinations}
        onNavigateToServices={navigateToServices}
        onNavigateToFAQ={navigateToFAQ}
        onNavigateToContact={navigateToContact} onNavigateToAbout={navigateToAbout}
        destinations={destinations}
      />
    );
  }

  // If on destinations page, render DestinationsPage
  if (currentRoute.type === 'destinations') {
    return (
      <DestinationsPage
        lang={lang}
        onNavigateHome={navigateHome}
        onNavigateToPackages={navigateToPackages}
        onNavigateToDestinations={navigateToAllDestinations}
        onNavigateToDestination={navigateToDestination}
        onNavigateToServices={navigateToServices}
        onNavigateToFAQ={navigateToFAQ}
        onNavigateToContact={navigateToContact} onNavigateToAbout={navigateToAbout}
      />
    );
  }

  // If on services page, render ServicesPage
  if (currentRoute.type === 'services') {
    return (
      <React.Suspense fallback={<PageLoader />}>
        <ServicesPage
          lang={lang}
          onNavigateHome={navigateHome}
          onNavigateToPackages={navigateToPackages}
          onNavigateToDestinations={navigateToAllDestinations}
          onNavigateToServices={navigateToServices}
          onNavigateToFAQ={navigateToFAQ}
          onNavigateToContact={navigateToContact} onNavigateToAbout={navigateToAbout}
        />
      </React.Suspense>
    );
  }

  // If on flight booking page, render FlightBookingPage
  if (currentRoute.type === 'flightBooking') {
    return (
      <React.Suspense fallback={<PageLoader />}>
        <FlightBookingPage
          lang={lang}
          onNavigateHome={navigateHome}
          onNavigateToPackages={navigateToPackages}
          onNavigateToDestinations={navigateToAllDestinations}
          onNavigateToServices={navigateToServices}
          onNavigateToFAQ={navigateToFAQ}
          onNavigateToContact={navigateToContact}
          onNavigateToAbout={navigateToAbout}
        />
      </React.Suspense>
    );
  }

  // If on hotel booking page, render HotelBookingPage
  if (currentRoute.type === 'hotelBooking') {
    return (
      <React.Suspense fallback={<PageLoader />}>
        <HotelBookingPage
          lang={lang}
          onNavigateHome={navigateHome}
          onNavigateToPackages={navigateToPackages}
          onNavigateToDestinations={navigateToAllDestinations}
          onNavigateToServices={navigateToServices}
          onNavigateToFAQ={navigateToFAQ}
          onNavigateToContact={navigateToContact}
          onNavigateToAbout={navigateToAbout}
        />
      </React.Suspense>
    );
  }

  // If on car rental page, render CarRentalPage
  if (currentRoute.type === 'carRental') {
    return (
      <React.Suspense fallback={<PageLoader />}>
        <CarRentalPage
          lang={lang}
          onNavigateHome={navigateHome}
          onNavigateToPackages={navigateToPackages}
          onNavigateToDestinations={navigateToAllDestinations}
          onNavigateToServices={navigateToServices}
          onNavigateToFAQ={navigateToFAQ}
          onNavigateToContact={navigateToContact}
          onNavigateToAbout={navigateToAbout}
        />
      </React.Suspense>
    );
  }

  // If on delegation reception page, render DelegationReceptionPage
  if (currentRoute.type === 'delegationReception') {
    return (
      <React.Suspense fallback={<PageLoader />}>
        <DelegationReceptionPage
          lang={lang}
          onNavigateHome={navigateHome}
          onNavigateToPackages={navigateToPackages}
          onNavigateToDestinations={navigateToAllDestinations}
          onNavigateToServices={navigateToServices}
          onNavigateToFAQ={navigateToFAQ}
          onNavigateToContact={navigateToContact}
          onNavigateToAbout={navigateToAbout}
        />
      </React.Suspense>
    );
  }

  // If on visa services page, render VisaServicesPage
  if (currentRoute.type === 'visaServices') {
    return (
      <React.Suspense fallback={<PageLoader />}>
        <VisaServicesPage
          lang={lang}
          onNavigateHome={navigateHome}
          onNavigateToPackages={navigateToPackages}
          onNavigateToDestinations={navigateToAllDestinations}
          onNavigateToServices={navigateToServices}
          onNavigateToFAQ={navigateToFAQ}
          onNavigateToContact={navigateToContact}
          onNavigateToAbout={navigateToAbout}
        />
      </React.Suspense>
    );
  }

  // If on train booking page
  if (currentRoute.type === 'trainBooking') {
    return (
      <React.Suspense fallback={<PageLoader />}>
        <TrainBookingPage
          lang={lang}
          onNavigateHome={navigateHome}
          onNavigateToPackages={navigateToPackages}
          onNavigateToDestinations={navigateToAllDestinations}
          onNavigateToServices={navigateToServices}
          onNavigateToFAQ={navigateToFAQ}
          onNavigateToContact={navigateToContact}
          onNavigateToAbout={navigateToAbout}
        />
      </React.Suspense>
    );
  }

  // If on football booking page
  if (currentRoute.type === 'footballBooking') {
    return (
      <React.Suspense fallback={<PageLoader />}>
        <FootballBookingPage
          lang={lang}
          onNavigateHome={navigateHome}
          onNavigateToPackages={navigateToPackages}
          onNavigateToDestinations={navigateToAllDestinations}
          onNavigateToServices={navigateToServices}
          onNavigateToFAQ={navigateToFAQ}
          onNavigateToContact={navigateToContact}
          onNavigateToAbout={navigateToAbout}
        />
      </React.Suspense>
    );
  }

  // If on trip organizing page
  if (currentRoute.type === 'tripOrganizing') {
    return (
      <React.Suspense fallback={<PageLoader />}>
        <TripOrganizingPage
          lang={lang}
          onNavigateHome={navigateHome}
          onNavigateToPackages={navigateToPackages}
          onNavigateToDestinations={navigateToAllDestinations}
          onNavigateToServices={navigateToServices}
          onNavigateToFAQ={navigateToFAQ}
          onNavigateToContact={navigateToContact}
          onNavigateToAbout={navigateToAbout}
        />
      </React.Suspense>
    );
  }

  // If on FAQ page, render FAQPage
  if (currentRoute.type === 'faq') {
    return (
      <React.Suspense fallback={<PageLoader />}>
        <FAQPage
          lang={lang}
          onNavigateHome={navigateHome}
          onNavigateToPackages={navigateToPackages}
          onNavigateToDestinations={navigateToAllDestinations}
          onNavigateToServices={navigateToServices}
          onNavigateToFAQ={navigateToFAQ}
          onNavigateToContact={navigateToContact} onNavigateToAbout={navigateToAbout}
        />
      </React.Suspense>
    );
  }

  // If on contact page, render ContactPage
  if (currentRoute.type === 'contact') {
    return (
      <React.Suspense fallback={<PageLoader />}>
        <ContactPage
          lang={lang}
          onNavigateHome={navigateHome}
          onNavigateToPackages={navigateToPackages}
          onNavigateToDestinations={navigateToAllDestinations}
          onNavigateToServices={navigateToServices}
          onNavigateToFAQ={navigateToFAQ}
          onNavigateToContact={navigateToContact}
          onNavigateToAbout={navigateToAbout}
        />
      </React.Suspense>
    );
  }

  // If on about page, render AboutPage
  if (currentRoute.type === 'about') {
    return (
      <React.Suspense fallback={<PageLoader />}>
        <AboutPage
          lang={lang}
          onNavigateHome={navigateHome}
          onNavigateToPackages={navigateToPackages}
          onNavigateToDestinations={navigateToAllDestinations}
          onNavigateToServices={navigateToServices}
          onNavigateToFAQ={navigateToFAQ}
          onNavigateToContact={navigateToContact}
          onNavigateToAbout={navigateToAbout}
        />
      </React.Suspense>
    );
  }

  // If on terms page, render TermsPage
  if (currentRoute.type === 'terms') {
    return (
      <React.Suspense fallback={<PageLoader />}>
        <TermsPage
          lang={lang}
          onNavigateHome={navigateHome}
          onNavigateToPackages={navigateToPackages}
          onNavigateToDestinations={navigateToAllDestinations}
          onNavigateToServices={navigateToServices}
          onNavigateToFAQ={navigateToFAQ}
          onNavigateToContact={navigateToContact}
          onNavigateToAbout={navigateToAbout}
        />
      </React.Suspense>
    );
  }

  // If on privacy page, render PrivacyPage
  if (currentRoute.type === 'privacy') {
    return (
      <React.Suspense fallback={<PageLoader />}>
        <PrivacyPage
          lang={lang}
          onNavigateHome={navigateHome}
          onNavigateToPackages={navigateToPackages}
          onNavigateToDestinations={navigateToAllDestinations}
          onNavigateToServices={navigateToServices}
          onNavigateToFAQ={navigateToFAQ}
          onNavigateToContact={navigateToContact}
          onNavigateToAbout={navigateToAbout}
        />
      </React.Suspense>
    );
  }

  // If on destinations page, render DestinationsPage
  if (currentRoute.type === 'destinations') {
    return (
      <DestinationsPage
        lang={lang}
        onNavigateHome={navigateHome}
        onNavigateToPackages={navigateToPackages}
        onNavigateToDestinations={navigateToAllDestinations}
        onNavigateToDestination={navigateToDestination}
        onNavigateToServices={navigateToServices}
        onNavigateToFAQ={navigateToFAQ}
        onNavigateToContact={navigateToContact}
        onNavigateToAbout={navigateToAbout}
      />
    );
  }

  // If on packages page, render PackagesPage
  if (currentRoute.type === 'packages') {
    return (
      <PackagesPage
        lang={lang}
        onNavigateHome={navigateHome}
        onNavigateToPackages={navigateToPackages}
        onNavigateToDestinations={navigateToAllDestinations}
        onNavigateToServices={navigateToServices}
        onNavigateToFAQ={navigateToFAQ}
        onNavigateToContact={navigateToContact}
        onNavigateToAbout={navigateToAbout}
        destinations={destinations}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      {/* Hero Background */}
      <div className="absolute top-0 left-0 w-full h-[100vh] z-0 overflow-hidden">
        {isMobile ? (
          <img
            src="/hero.webp"
            alt="Hero Background"
            className="w-full h-full object-cover"
            fetchPriority="high"
            decoding="sync"
            width="800"
            height="1200"
          />
        ) : (
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            poster="/hero.webp"
          >
            <source src="/hero.mp4" type="video/mp4" />
          </video>
        )}
        <div className="absolute inset-0 bg-black/40" />
      </div>

      <Navbar lang={lang} onNavigateHome={navigateHome} onNavigateToPackages={navigateToPackages} onNavigateToDestinations={navigateToAllDestinations} onNavigateToServices={navigateToServices} onNavigateToFAQ={navigateToFAQ} onNavigateToContact={navigateToContact} onNavigateToAbout={navigateToAbout} />

      <main className="relative z-10 pt-32 px-4 pb-20 flex flex-col items-center min-h-screen">
        <div className="text-center mb-8 max-w-4xl mx-auto flex flex-col items-center">
          {/* Added COMO Logo - GIF for Safari, WebM for others */}
          {isSafari ? (
            <img
              src="/assets/videos/commo.gif"
              alt="COMO Logo"
              className="w-20 md:w-24 mb-4 object-contain"
              width="80"
              height="80"
            />
          ) : (
            <video
              autoPlay
              loop
              muted
              playsInline
              className="w-20 md:w-24 mb-4"
              width="80"
              height="80"
            >
              <source src="/assets/videos/commo.webm" type="video/webm" />
            </video>
          )}

          {/* Adjusted Title size and line height */}
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-black text-white !leading-[1.3] drop-shadow-xl whitespace-pre-line">
            {t.hero.headline}
          </h1>
        </div>

        <BookingWidget lang={lang} />

        <RatingsBanner />

        {/* Content Sections Placeholder - to make the page scrollable and complete visually */}
        <div className="w-full mt-12 space-y-24 relative z-30">

          {/* Destinations - Redesigned & Centered */}
          <section className="py-12 bg-white">
            <div className="max-w-[1300px] mx-auto px-6 md:px-12">
              {/* New Section Title */}
              <div className="mb-8">
                <span className="text-blue-600 font-bold text-sm tracking-wider uppercase">
                  {lang === 'ar' ? 'اكتشف العالم' : 'Explore the World'}
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mt-2">
                  {lang === 'ar' ? 'اختر وجهتك' : 'Choose your Destination'}
                </h2>
              </div>

              <div className="relative rounded-[1.5rem] overflow-hidden min-h-[400px] md:min-h-[450px]">
                <div className="absolute inset-0">
                  <img
                    src="/assets/images/destinations_banner.webp"
                    srcSet="/assets/images/destinations_banner_mobile.webp 600w, /assets/images/destinations_banner.webp 1260w"
                    sizes="(max-width: 768px) 100vw, 1260px"
                    alt="Destinations Banner"
                    className="w-full h-full object-cover"
                    width="1260"
                    height="750"
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-r ${lang === 'ar' ? 'bg-gradient-to-l' : 'bg-gradient-to-r'} from-black/60 via-black/20 to-transparent`}></div>
                </div>

                <div className="relative z-10 p-8 md:p-16 h-full flex flex-col items-start justify-center pb-32">
                  <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">{t.destinations.title}</h2>
                  <p className="text-white/90 text-lg mb-8 font-medium">{t.destinations.subtitle}</p>
                  <button onClick={navigateToPackages} className="bg-white text-gray-900 px-8 py-3.5 rounded-full font-bold text-sm hover:bg-gray-50 transition-colors shadow-lg active:scale-95 cursor-pointer border-none">
                    {t.destinations.cta}
                  </button>
                </div>

                <div className={`absolute ${lang === 'ar' ? 'left-8' : 'right-8'} top-1/2 -translate-y-1/2 hidden md:flex gap-3 z-10`}>
                  <button
                    onClick={() => scrollDestinations('right')}
                    className={`w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white text-gray-900 transition-colors shadow-lg active:scale-95 ${!canScrollDestinationsRight ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={!canScrollDestinationsRight}
                    aria-label="Scroll destinations right"
                  >
                    <ChevronLeft className={`w-6 h-6 ${lang === 'ar' ? 'rotate-180' : ''}`} />
                  </button>
                  <button
                    onClick={() => scrollDestinations('left')}
                    className={`w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white text-gray-900 transition-colors shadow-lg active:scale-95 ${!canScrollDestinationsLeft ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={!canScrollDestinationsLeft}
                    aria-label="Scroll destinations left"
                  >
                    <ChevronRight className={`w-6 h-6 ${lang === 'ar' ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              </div>

              <div className="relative z-20 -mt-24 px-4 md:px-12">
                {/* Navigation Button - Left - Mobile & Desktop */}
                {canScrollDestinationsLeft && (
                  <div className={`absolute -left-2 md:-left-4 top-1/2 -translate-y-1/2 z-30 animate-in fade-in zoom-in duration-200 ${hasScrolledDestinations ? '' : 'md:hidden'}`}>
                    <button
                      onClick={() => scrollDestinations('left')}
                      className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-full flex items-center justify-center hover:scale-105 transition-all border border-gray-200 shadow-lg"
                      aria-label="Scroll destinations left"
                    >
                      <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-700" />
                    </button>
                  </div>
                )}

                {/* Navigation Button - Right - Mobile & Desktop */}
                {canScrollDestinationsRight && (
                  <div className={`absolute -right-2 md:-right-4 top-1/2 -translate-y-1/2 z-30 animate-in fade-in zoom-in duration-200 ${hasScrolledDestinations ? '' : 'md:hidden'}`}>
                    <button
                      onClick={() => scrollDestinations('right')}
                      className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-full flex items-center justify-center hover:scale-105 transition-all border border-gray-200 shadow-lg"
                      aria-label="Scroll destinations right"
                    >
                      <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-gray-700" />
                    </button>
                  </div>
                )}

                {/* Scroll Container */}
                <div
                  ref={destinationsScrollRef}
                  onScroll={checkDestinationsScroll}
                  className="flex overflow-x-auto gap-4 md:gap-6 pb-4 no-scrollbar snap-x"
                >
                  {destinationsLoading ? (
                    <div className="flex-shrink-0 w-full text-center py-8 text-gray-500">
                      {lang === 'ar' ? 'جاري التحميل...' : 'Loading...'}
                    </div>
                  ) : destinations.slice(0, 10).map((dest) => (
                    <div
                      key={dest.id}
                      className="group cursor-pointer flex flex-col gap-3 flex-shrink-0 w-[160px] md:w-[200px] snap-start"
                      onClick={() => navigateToDestination(dest)}
                    >
                      <div className="aspect-[4/3] rounded-2xl overflow-hidden border-[6px] border-white shadow-xl relative bg-white">
                        <img
                          src={dest.image}
                          srcSet={
                            dest.image.includes('unsplash.com') || dest.image.includes('pexels.com')
                              ? `${dest.image.replace(/w=\d+/, 'w=300')} 300w, ${dest.image.replace(/w=\d+/, 'w=500')} 500w, ${dest.image} 800w`
                              : undefined
                          }
                          sizes="(max-width: 768px) 160px, 200px"
                          alt={dest.name}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                      </div>
                      <span className={`font-bold text-gray-900 text-sm md:text-base ${lang === 'ar' ? 'pr-2' : 'pl-2'}`}>
                        {dest.name}
                      </span>
                    </div>
                  ))}

                  {/* View All Destinations Button */}
                  {!destinationsLoading && destinations.length > 10 && (
                    <div
                      className="group cursor-pointer flex flex-col gap-3 flex-shrink-0 w-[160px] md:w-[200px] snap-start"
                      onClick={navigateToAllDestinations}
                    >
                      <div className="aspect-[4/3] rounded-2xl overflow-hidden border-[6px] border-white shadow-xl relative bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-700 flex items-center justify-center group-hover:from-emerald-600 group-hover:to-emerald-800 transition-all">
                        <div className="text-center p-4">
                          <div className="w-12 h-12 mx-auto mb-2 bg-white/20 rounded-full flex items-center justify-center">
                            <ArrowRight className={`w-6 h-6 text-white ${lang === 'ar' ? 'rotate-180' : ''}`} />
                          </div>
                          <span className="text-white font-bold text-sm">
                            {lang === 'ar' ? 'عرض الكل' : 'View All'}
                          </span>
                        </div>
                      </div>
                      <span className={`font-bold text-gray-900 text-sm md:text-base ${lang === 'ar' ? 'pr-2' : 'pl-2'}`}>
                        {lang === 'ar' ? `+${destinations.length - 10} وجهة` : `+${destinations.length - 10} more`}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Activities Section */}
          <section className="relative w-full max-w-[1300px] mx-auto px-6 md:px-12 group/section">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900">{t.activities.title}</h2>
            </div>

            <div className="relative">
              {/* Navigation Button - Left */}
              {canScrollLeft && (
                <div className="absolute -left-6 md:-left-8 top-1/2 -translate-y-1/2 z-20 animate-in fade-in zoom-in duration-200">
                  <button
                    onClick={() => scrollActivities('left')}
                    className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-full flex items-center justify-center hover:scale-105 transition-all border border-gray-200"
                    aria-label="Scroll activities left"
                  >
                    <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-700" />
                  </button>
                </div>
              )}

              {/* Navigation Button - Right */}
              {canScrollRight && (
                <div className="absolute -right-6 md:-right-8 top-1/2 -translate-y-1/2 z-20 animate-in fade-in zoom-in duration-200">
                  <button
                    onClick={() => scrollActivities('right')}
                    className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-full flex items-center justify-center hover:scale-105 transition-all border border-gray-200"
                    aria-label="Scroll activities right"
                  >
                    <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-gray-700" />
                  </button>
                </div>
              )}

              {/* Scroll Container */}
              <div
                ref={activitiesScrollRef}
                onScroll={checkScroll}
                className="flex overflow-x-auto gap-6 pb-8 -mx-4 px-4 md:mx-0 md:px-0 no-scrollbar snap-x"
              >
                {getLocalizedActivities(lang).map((activity) => (
                  <ActivityCard key={activity.id} activity={activity} lang={lang} />
                ))}
              </div>
            </div>
          </section>

          {/* Packages - Updated to Custom Design */}
          <section className="relative w-full max-w-[1300px] mx-auto px-6 md:px-12 group/section">
            <div className="mb-8">
              <span className="text-blue-600 font-bold text-sm tracking-wider uppercase">{TRANSLATIONS[lang].packages.eyebrow}</span>
              <h2 className="text-3xl font-bold text-gray-900 mt-2">{TRANSLATIONS[lang].packages.title}</h2>
            </div>

            <div className="relative">
              {/* Navigation Button - Left */}
              <div className={`absolute -left-6 md:-left-8 top-1/2 -translate-y-1/2 z-20 hidden md:block ${canScrollPackagesLeft ? 'animate-in fade-in zoom-in duration-200' : ''}`}>
                <button
                  onClick={() => scrollPackages('left')}
                  disabled={!canScrollPackagesLeft}
                  className={`w-10 h-10 md:w-12 md:h-12 bg-white rounded-full flex items-center justify-center transition-all border border-gray-200 ${canScrollPackagesLeft ? 'hover:scale-105' : 'opacity-40 cursor-not-allowed'}`}
                  aria-label="Scroll packages left"
                >
                  <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-700" />
                </button>
              </div>
              {canScrollPackagesLeft && (
                <div className="absolute -left-6 md:hidden top-1/2 -translate-y-1/2 z-20 animate-in fade-in zoom-in duration-200">
                  <button
                    onClick={() => scrollPackages('left')}
                    className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:scale-105 transition-all border border-gray-200"
                    aria-label="Scroll packages left"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-700" />
                  </button>
                </div>
              )}

              {/* Navigation Button - Right */}
              <div className={`absolute -right-6 md:-right-8 top-1/2 -translate-y-1/2 z-20 hidden md:block ${canScrollPackagesRight ? 'animate-in fade-in zoom-in duration-200' : ''}`}>
                <button
                  onClick={() => scrollPackages('right')}
                  disabled={!canScrollPackagesRight}
                  className={`w-10 h-10 md:w-12 md:h-12 bg-white rounded-full flex items-center justify-center transition-all border border-gray-200 ${canScrollPackagesRight ? 'hover:scale-105' : 'opacity-40 cursor-not-allowed'}`}
                  aria-label="Scroll packages right"
                >
                  <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-gray-700" />
                </button>
              </div>
              {canScrollPackagesRight && (
                <div className="absolute -right-6 md:hidden top-1/2 -translate-y-1/2 z-20 animate-in fade-in zoom-in duration-200">
                  <button
                    onClick={() => scrollPackages('right')}
                    className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:scale-105 transition-all border border-gray-200"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-700" />
                  </button>
                </div>
              )}

              <div
                ref={packagesScrollRef}
                onScroll={checkPackagesScroll}
                className="flex overflow-x-auto gap-8 pb-8 -mx-4 px-4 md:mx-0 md:px-0 no-scrollbar snap-x items-stretch"
              >
                {homePackagesLoading ? (
                  <div className="min-w-full text-center py-8 text-gray-500">
                    {lang === 'ar' ? 'جاري التحميل...' : 'Loading...'}
                  </div>
                ) : (
                  <>
                    {homePackages.slice(0, 9).map(pkg => (
                      <div key={pkg.id} className="min-w-[300px] md:min-w-[380px] snap-center">
                        <PackageCard pkg={pkg} t={t} lang={lang} />
                      </div>
                    ))}
                    {/* See More Button */}
                    {homePackages.length > 0 && (
                      <div className="min-w-[200px] snap-center flex items-center justify-center">
                        <button
                          onClick={navigateToPackages}
                          className="flex flex-col items-center justify-center gap-3 px-8 py-6 bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-2xl font-bold transition-all shadow-lg hover:shadow-xl active:scale-95"
                        >
                          <ChevronRight className={`w-8 h-8 ${lang === 'en' ? '' : 'rotate-180'}`} />
                          <span className="text-lg">{lang === 'ar' ? 'عرض المزيد' : 'See More'}</span>
                          <span className="text-xs opacity-75">{lang === 'ar' ? `${homePackages.length} عرض` : `${homePackages.length} offers`}</span>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>

          {/* New Payment Options Section - Moved Here */}
          <section className="relative w-full max-w-[1300px] mx-auto px-6 md:px-12">
            <div className="flex justify-start mb-8">
              <h2 className="text-2xl font-bold text-gray-900">{t.payment.title}</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {getLocalizedPaymentOptions(lang).map((option) => (
                <div key={option.id} className="flex flex-col gap-4">
                  {/* Card Image */}
                  <div className="w-full aspect-[16/10] rounded-[1rem] overflow-hidden relative border border-gray-100 shadow-sm">
                    <img
                      src={option.image}
                      srcSet={option.imageSmall ? `${option.imageSmall} 400w, ${option.image} 800w` : undefined}
                      alt={option.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                    {/* Mock Logo Overlay */}
                    <div className="absolute bottom-4 right-4 text-white font-black text-xl drop-shadow-lg tracking-tight z-10">
                      {option.logoText}
                    </div>
                    <div className={`absolute inset-0 bg-gradient-to-t ${lang === 'ar' ? 'from-black/60' : 'from-black/60'} to-transparent opacity-60`}></div>
                  </div>

                  {/* Text Content */}
                  <div className="text-center px-2">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{option.title}</h3>
                    <p className="text-sm text-gray-500">{option.subtitle}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/+9651811008"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent('whatsapp_floating')}
        className="fixed bottom-6 left-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 group"
        style={{ direction: 'ltr' }}
        title={lang === 'ar' ? 'تواصل معنا عبر واتساب' : 'Chat with us on WhatsApp'}
      >
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-pulse">
          {lang === 'ar' ? 'محادثة' : 'Chat'}
        </span>
      </a>

      {/* Footer */}
      <Footer lang={lang} />
    </div>
  );
};

export default App;